import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { FanLeadAccountMenuReact } from '@fanlead/account-widget/react';
import type { FanLeadAvatarEmoji } from '@fanlead/account-widget';
import { standardFollowUpPrompt } from '@salesai/shared';
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Bot,
  Check,
  CheckCheck,
  ChevronDown,
  CircleDollarSign,
  Clock,
  Copy,
  FileText,
  Eye,
  EyeOff,
  Hash,
  HelpCircle,
  Inbox,
  KeyRound,
  LayoutDashboard,
  Menu,
  MessageCircle,
  MessageSquareText,
  Monitor,
  Moon,
  Music,
  Pause,
  Pencil,
  Play,
  PlayCircle,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  Tags,
  Trash2,
  UserPlus,
  Users,
  WandSparkles,
  X,
  Zap,
} from 'lucide-react';
import { api, loadSession } from './api.js';
import { demoConversations, demoMessages, demoOverview, demoProject, demoSession } from './demo.js';
import type {
  ChatMessage,
  AmoCrmIntegrationStatus,
  Conversation,
  ConversationNote,
  ConversationSearchResult,
  FollowUpStep,
  Overview,
  Project,
  ProjectAccessEntry,
  Session,
} from './types.js';

type Tab = 'dialogs' | 'analytics' | 'projects' | 'settings';
type SettingsSection = 'channels' | 'access' | 'prompts' | 'comments' | 'integrations' | 'labels' | 'openai';
type ThemePreference = 'system' | 'light' | 'dark';
type ToastKind = 'success' | 'error' | 'info';
type Notify = (message: string, kind?: ToastKind) => void;
type SettingsAction = (work: () => Promise<unknown>, successMessage?: string) => void;

interface AppToast {
  id: number;
  message: string;
  kind: ToastKind;
}

interface CabinetRoute {
  tab: Tab;
  projectSlug?: string;
  settingsSection: SettingsSection;
  conversationId?: string;
}

function ToastItem({ toast, onDismiss }: { toast: AppToast; onDismiss: (id: number) => void }) {
  useEffect(() => {
    const timeout = window.setTimeout(() => onDismiss(toast.id), 5_000);
    return () => window.clearTimeout(timeout);
  }, [onDismiss, toast.id]);
  return (
    <div className={`toast toast--${toast.kind}`} role={toast.kind === 'error' ? 'alert' : 'status'}>
      <span className="toast__icon">
        {toast.kind === 'success' ? <Check /> : toast.kind === 'error' ? <X /> : <HelpCircle />}
      </span>
      <span>{toast.message}</span>
      <button type="button" aria-label="Закрыть уведомление" onClick={() => onDismiss(toast.id)}>
        <X />
      </button>
    </div>
  );
}

function ToastStack({ toasts, onDismiss }: { toasts: AppToast[]; onDismiss: (id: number) => void }) {
  return (
    <div className="toast-stack" aria-live="polite" aria-label="Уведомления">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function parseCabinetRoute(pathname: string): CabinetRoute {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'app' || parts.length === 1) return { tab: 'dialogs', settingsSection: 'channels' };
  if (parts[1] === 'projects') return { tab: 'projects', settingsSection: 'channels' };
  const projectSlug = parts[1];
  if (parts[2] === 'analytics') return { tab: 'analytics', projectSlug, settingsSection: 'channels' };
  if (parts[2] === 'settings') {
    const section = parts[3];
    const settingsSection: SettingsSection =
      section === 'agents' || section === 'prompts'
        ? 'prompts'
        : section === 'openai' ||
            section === 'channels' ||
            section === 'comments' ||
            section === 'integrations' ||
            section === 'labels' ||
            section === 'access'
          ? section
          : 'channels';
    return { tab: 'settings', projectSlug, settingsSection };
  }
  return {
    tab: 'dialogs',
    projectSlug,
    settingsSection: 'channels',
    ...(parts[2] === 'dialogs' && parts[3] ? { conversationId: parts[3] } : {}),
  };
}

function projectPath(projectSlug: string, tab: Tab, section: SettingsSection = 'channels'): string {
  if (tab === 'projects') return '/app/projects';
  if (tab === 'settings') return `/app/${projectSlug}/settings/${section === 'prompts' ? 'agents' : section}`;
  return `/app/${projectSlug}/${tab}`;
}

function pageTitle(route: CabinetRoute): string {
  if (route.tab === 'dialogs') return 'Диалоги';
  if (route.tab === 'analytics') return 'Аналитика продаж';
  if (route.tab === 'projects') return 'Проекты';
  if (route.settingsSection === 'prompts') return 'AI-агенты продаж';
  if (route.settingsSection === 'comments') return 'AI-комментарии';
  if (route.settingsSection === 'integrations') return 'Интеграции';
  if (route.settingsSection === 'access') return 'Доступы';
  if (route.settingsSection === 'labels') return 'Метки диалогов';
  if (route.settingsSection === 'openai') return 'OpenAI и стоимость';
  return 'Каналы';
}

function pageDescription(route: CabinetRoute): string {
  if (route.tab === 'dialogs') return 'Входящие и исходящие сообщения выбранного проекта и канала.';
  if (route.tab === 'analytics') return 'Диалоги, сообщения, квалификация лидов и расход токенов.';
  if (route.tab === 'projects') return 'Отдельные рабочие пространства SalesAI.';
  if (route.settingsSection === 'prompts') return 'Инструкции AI-агентов, дожимы и назначение каналов.';
  if (route.settingsSection === 'comments') return 'Автоматические ответы на комментарии VK по вашему промпту.';
  if (route.settingsSection === 'integrations') return 'Подключения CRM и внешних сервисов.';
  if (route.settingsSection === 'access') return 'Пользователи, роли и доступные каналы.';
  if (route.settingsSection === 'labels') return 'Проектные метки для сортировки и работы с лидами.';
  if (route.settingsSection === 'openai') return 'Ключ, модель по умолчанию и оценка стоимости.';
  return 'Подключения VK и Telegram внутри выбранного проекта.';
}

function formatDialogCount(count: number): string {
  const lastTwoDigits = count % 100;
  const lastDigit = count % 10;
  const noun =
    lastTwoDigits >= 11 && lastTwoDigits <= 14
      ? 'диалогов'
      : lastDigit === 1
        ? 'диалог'
        : lastDigit >= 2 && lastDigit <= 4
          ? 'диалога'
          : 'диалогов';

  return `${count} ${noun}`;
}

function formatChannelCount(count: number): string {
  const lastTwoDigits = count % 100;
  const lastDigit = count % 10;
  const noun =
    lastTwoDigits >= 11 && lastTwoDigits <= 14
      ? 'каналов'
      : lastDigit === 1
        ? 'канал'
        : lastDigit >= 2 && lastDigit <= 4
          ? 'канала'
          : 'каналов';

  return `${count} ${noun}`;
}

function formatFollowUpDate(value?: string | null): string {
  if (!value) return 'дата не указана';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'дата не указана';
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function formatAiCost(value?: string | number | null): string {
  const cost = Number(value ?? 0);
  if (!Number.isFinite(cost) || cost <= 0) return '$0.000000';
  return `$${cost < 0.01 ? cost.toFixed(6) : cost.toFixed(4)}`;
}

function formatTokenCount(value?: string | number | null): string {
  const tokens = Number(value ?? 0);
  if (!Number.isFinite(tokens)) return '0';
  return new Intl.NumberFormat('ru-RU').format(tokens);
}

function MessageUsageBadge({
  usage,
  technical = false,
}: {
  usage: NonNullable<ChatMessage['usage']>;
  technical?: boolean;
}) {
  const input = Number(usage.inputTokens || 0);
  const cached = Number(usage.cachedInputTokens || 0);
  const cacheWrite = Number(usage.cacheWriteInputTokens || 0);
  const output = Number(usage.outputTokens || 0);
  const reasoning = Number(usage.reasoningTokens || 0);
  const freshInput = Math.max(0, input - cached - cacheWrite);
  const cost = usage.costCalculated ? formatAiCost(usage.estimatedCost) : 'Не рассчитана';
  const compactCost = usage.costCalculated ? formatAiCost(usage.estimatedCost) : 'нет тарифа';
  const label = technical ? `${compactCost} · ${formatTokenCount(usage.totalTokens)} ток.` : compactCost;

  return (
    <span
      className={`message-usage ${technical ? 'message-usage--technical' : ''}`}
      tabIndex={0}
      aria-label={`Расход OpenAI: ${cost}, ${formatTokenCount(usage.totalTokens)} токенов`}
    >
      <span className="message-usage__label">{label}</span>
      <span className="message-usage__popover" role="tooltip">
        <strong>Расход OpenAI</strong>
        <span className="message-usage__cost">
          <i>Стоимость</i>
          <b>{cost}</b>
        </span>
        {!usage.costCalculated && <small>Заполните тариф модели в настройках OpenAI.</small>}
        <span className="message-usage__divider" />
        <span>
          <i>Всего токенов</i>
          <b>{formatTokenCount(usage.totalTokens)}</b>
        </span>
        <span>
          <i>Вход</i>
          <b>{formatTokenCount(input)}</b>
        </span>
        <span className="message-usage__subrow">
          <i>новый вход</i>
          <b>{formatTokenCount(freshInput)}</b>
        </span>
        <span className="message-usage__subrow">
          <i>из кэша</i>
          <b>{formatTokenCount(cached)}</b>
        </span>
        {cacheWrite > 0 && (
          <span className="message-usage__subrow">
            <i>записано в кэш</i>
            <b>{formatTokenCount(cacheWrite)}</b>
          </span>
        )}
        <span>
          <i>Выход</i>
          <b>{formatTokenCount(output)}</b>
        </span>
        {reasoning > 0 && (
          <span className="message-usage__subrow">
            <i>из них рассуждение</i>
            <b>{formatTokenCount(reasoning)}</b>
          </span>
        )}
        <span className="message-usage__divider" />
        <span>
          <i>API-запросов</i>
          <b>{usage.calls}</b>
        </span>
        {usage.hiddenCalls > 0 && (
          <span className="message-usage__subrow">
            <i>повторных или внутренних</i>
            <b>{usage.hiddenCalls}</b>
          </span>
        )}
      </span>
    </span>
  );
}

type FollowUpUnit = 'minutes' | 'hours' | 'days';
type FollowUpStepDraft = Omit<FollowUpStep, 'delayMinutes'> & { delayValue: number; delayUnit: FollowUpUnit };

const defaultFollowUpSteps: FollowUpStep[] = [
  { stepOrder: 1, delayMinutes: 30, useStandardPrompt: true },
  { stepOrder: 2, delayMinutes: 1440, useStandardPrompt: true },
  { stepOrder: 3, delayMinutes: 4320, useStandardPrompt: true },
  { stepOrder: 4, delayMinutes: 10080, useStandardPrompt: true },
  { stepOrder: 5, delayMinutes: 20160, useStandardPrompt: true },
];

const channelTimeZones = [
  ['Europe/Kaliningrad', 'Калининград'],
  ['Europe/Moscow', 'Москва'],
  ['Europe/Samara', 'Самара'],
  ['Asia/Yekaterinburg', 'Екатеринбург'],
  ['Asia/Omsk', 'Омск'],
  ['Asia/Krasnoyarsk', 'Красноярск'],
  ['Asia/Irkutsk', 'Иркутск'],
  ['Asia/Yakutsk', 'Якутск'],
  ['Asia/Vladivostok', 'Владивосток'],
  ['Asia/Magadan', 'Магадан'],
  ['Asia/Kamchatka', 'Камчатка'],
  ['Europe/Amsterdam', 'Амстердам'],
  ['Europe/Berlin', 'Берлин'],
] as const;
const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const initialFollowUpStepCount = 3;
const maxFollowUpStepCount = defaultFollowUpSteps.length;

function followUpStepDraft(step: FollowUpStep): FollowUpStepDraft {
  const delayUnit: FollowUpUnit =
    step.delayMinutes % 1440 === 0 ? 'days' : step.delayMinutes % 60 === 0 ? 'hours' : 'minutes';
  const divisor = delayUnit === 'days' ? 1440 : delayUnit === 'hours' ? 60 : 1;
  return {
    ...step,
    prompt: step.prompt ?? (step.useStandardPrompt ? standardFollowUpPrompt : null),
    delayValue: step.delayMinutes / divisor,
    delayUnit,
  };
}

function normalizeFollowUpSteps(steps: FollowUpStep[]): FollowUpStepDraft[] {
  const source =
    steps.length > 0 ? steps.slice(0, maxFollowUpStepCount) : defaultFollowUpSteps.slice(0, initialFollowUpStepCount);
  return source.map((step, index) => followUpStepDraft({ ...step, stepOrder: index + 1 }));
}

function followUpCountLabel(count: number): string {
  if (count === 1) return '1 дожим';
  if (count >= 2 && count <= 4) return `${count} дожима`;
  return `${count} дожимов`;
}

function followUpStepPayload(step: FollowUpStepDraft): FollowUpStep {
  const multiplier = step.delayUnit === 'days' ? 1440 : step.delayUnit === 'hours' ? 60 : 1;
  return {
    stepOrder: step.stepOrder,
    delayMinutes: Math.max(1, Math.min(43_200, Math.round(step.delayValue * multiplier))),
    useStandardPrompt: step.useStandardPrompt,
    prompt: step.useStandardPrompt ? null : step.prompt?.trim() || null,
  };
}

function formatProjectCount(count: number): string {
  const lastTwoDigits = count % 100;
  const lastDigit = count % 10;
  const noun =
    lastTwoDigits >= 11 && lastTwoDigits <= 14
      ? 'проектов'
      : lastDigit === 1
        ? 'проект'
        : lastDigit >= 2 && lastDigit <= 4
          ? 'проекта'
          : 'проектов';

  return `${count} ${noun}`;
}

interface HeaderSelectOption {
  value: string;
  label: string;
  detail?: string;
  imageUrl?: string;
}

function HeaderSelect({
  label,
  value,
  options,
  icon,
  onChange,
}: {
  label: string;
  value: string;
  options: HeaderSelectOption[];
  icon: ReactNode;
  onChange: (value: string) => void;
}) {
  const details = useRef<HTMLDetailsElement>(null);
  const selected = options.find((option) => option.value === value) ?? options[0];
  return (
    <details className="header-select" ref={details}>
      <summary>
        <span className="header-select__icon">
          {selected?.imageUrl ? <img src={selected.imageUrl} alt="" /> : icon}
        </span>
        <span>
          <small>{label}</small>
          <b>{selected?.label ?? 'Не выбрано'}</b>
        </span>
        <ChevronDown size={15} />
      </summary>
      <div className="header-select__menu">
        {options.map((option) => (
          <button
            type="button"
            className={option.value === value ? 'active' : ''}
            key={option.value}
            onClick={() => {
              onChange(option.value);
              details.current?.removeAttribute('open');
            }}
          >
            {option.imageUrl ? <img src={option.imageUrl} alt="" /> : <span>{option.label.charAt(0)}</span>}
            <span>
              <b>{option.label}</b>
              {option.detail && <small>{option.detail}</small>}
            </span>
            {option.value === value && <Check size={15} />}
          </button>
        ))}
      </div>
    </details>
  );
}

function CompactSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string; detail?: string }>;
  onChange: (value: string) => void;
}) {
  const details = useRef<HTMLDetailsElement>(null);
  const selected = options.find((option) => option.value === value) ?? options[0];
  return (
    <label className="compact-select">
      <span>{label}</span>
      <details ref={details}>
        <summary>
          <b>{selected?.label ?? 'Не выбрано'}</b>
          <ChevronDown size={15} />
        </summary>
        <div className="compact-select__menu">
          {options.map((option) => (
            <button
              type="button"
              className={option.value === value ? 'active' : ''}
              key={option.value}
              onClick={() => {
                onChange(option.value);
                details.current?.removeAttribute('open');
              }}
            >
              <span>
                <b>{option.label}</b>
                {option.detail && <small>{option.detail}</small>}
              </span>
              {option.value === value && <Check size={14} />}
            </button>
          ))}
        </div>
      </details>
    </label>
  );
}

function ProjectMultiSelect({
  projects,
  selectedIds,
  currentProjectId,
  onChange,
}: {
  projects: Project[];
  selectedIds: string[];
  currentProjectId: string;
  onChange: (projectIds: string[]) => void;
}) {
  const allSelected = projects.length > 1 && selectedIds.length === projects.length;
  const selectedProject =
    selectedIds.length === 1 ? projects.find((project) => project.id === selectedIds[0]) : undefined;
  const summary = allSelected ? 'Все проекты' : (selectedProject?.name ?? formatProjectCount(selectedIds.length));
  const toggleProject = (projectId: string) => {
    const selected = selectedIds.includes(projectId);
    if (selected && selectedIds.length === 1) return;
    onChange(selected ? selectedIds.filter((id) => id !== projectId) : [...selectedIds, projectId]);
  };

  return (
    <label className="compact-select project-multi-select">
      <span>Проекты</span>
      <details>
        <summary>
          <b>{summary}</b>
          <ChevronDown size={15} />
        </summary>
        <div className="compact-select__menu project-multi-select__menu">
          {projects.length > 1 && (
            <>
              <button
                type="button"
                className={allSelected ? 'active' : ''}
                aria-pressed={allSelected}
                onClick={() => onChange(projects.map((project) => project.id))}
              >
                <span>
                  <b>Все проекты</b>
                  <small>
                    {formatDialogCount(projects.reduce((sum, project) => sum + project.conversationCount, 0))}
                  </small>
                </span>
                {allSelected && <Check size={14} />}
              </button>
              <div className="project-multi-select__divider" />
            </>
          )}
          {projects.map((project) => {
            const selected = selectedIds.includes(project.id);
            return (
              <button
                type="button"
                className={selected ? 'active' : ''}
                aria-pressed={selected}
                key={project.id}
                onClick={() => toggleProject(project.id)}
              >
                <span>
                  <b>
                    {project.name}
                    {project.id === currentProjectId && <em>Текущий</em>}
                  </b>
                  <small>{formatDialogCount(project.conversationCount)}</small>
                </span>
                <i className="project-multi-select__check">{selected && <Check size={13} />}</i>
              </button>
            );
          })}
        </div>
      </details>
    </label>
  );
}

interface CustomSelectOption {
  value: string;
  label: string;
  detail?: string;
}

function CustomSelect({
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel,
}: {
  value: string;
  options: CustomSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const listboxId = useId();
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const selected = options[selectedIndex];

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const focusOption = (index: number) => {
    const nextIndex = (index + options.length) % options.length;
    requestAnimationFrame(() => optionRefs.current[nextIndex]?.focus());
  };

  const openFromKeyboard = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    setOpen(true);
    if (event.key === 'ArrowUp' || event.key === 'End') focusOption(options.length - 1);
    else if (event.key === 'Home') focusOption(0);
    else focusOption(selectedIndex);
  };

  const navigateOptions = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'Home') focusOption(0);
    else if (event.key === 'End') focusOption(options.length - 1);
    else focusOption(index + (event.key === 'ArrowDown' ? 1 : -1));
  };

  return (
    <div className={`custom-select${open ? ' custom-select--open' : ''}`} ref={root}>
      <button
        type="button"
        className="custom-select__trigger"
        ref={trigger}
        role="combobox"
        aria-label={ariaLabel}
        aria-controls={listboxId}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={openFromKeyboard}
      >
        <span>{selected?.label ?? 'Не выбрано'}</span>
        <ChevronDown size={16} />
      </button>
      {open && (
        <div className="custom-select__menu" id={listboxId} role="listbox" aria-label={ariaLabel}>
          {options.map((option, index) => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`custom-select__option${option.value === value ? ' active' : ''}`}
              key={option.value}
              ref={(element) => {
                optionRefs.current[index] = element;
              }}
              onKeyDown={(event) => navigateOptions(event, index)}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
                trigger.current?.focus();
              }}
            >
              <span>
                <b>{option.label}</b>
                {option.detail && <small>{option.detail}</small>}
              </span>
              {option.value === value && <Check size={15} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const demoMode = import.meta.env.DEV && new URLSearchParams(location.search).get('demo') === '1';

const OPENAI_MODELS = [
  { id: 'gpt-6-luna', label: 'GPT-6 Luna · быстро и экономно' },
  { id: 'gpt-6-sol', label: 'GPT-6 Sol · баланс качества и цены' },
  { id: 'gpt-6-astra', label: 'GPT-6 Astra · максимальное качество' },
  { id: 'gpt-5.6-luna', label: 'GPT-5.6 Luna' },
  { id: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' },
  { id: 'gpt-5.6-sol', label: 'GPT-5.6 Sol' },
  { id: 'gpt-5-mini', label: 'GPT-5 mini · совместимость' },
];

const OPENAI_MODEL_RATES: Record<string, { input: string; cached: string; output: string }> = {
  'gpt-6-astra': { input: '$5.00', cached: '$0.50', output: '$25.00' },
  'gpt-6-sol': { input: '$1.00', cached: '$0.10', output: '$5.00' },
  'gpt-6-luna': { input: '$0.05', cached: '$0.005', output: '$0.25' },
  'gpt-5.6-sol': { input: '$4.00', cached: '$0.40', output: '$20.00' },
  'gpt-5-mini': { input: '$0.25', cached: '$0.025', output: '$2.00' },
};

function ModelSelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const options = OPENAI_MODELS.some((option) => option.id === value)
    ? OPENAI_MODELS
    : [{ id: value, label: `${value} · текущая` }, ...OPENAI_MODELS];
  return (
    <CustomSelect
      value={value}
      options={options.map((option) => ({ value: option.id, label: option.label }))}
      onChange={onChange}
      disabled={disabled}
      ariaLabel="Выберите модель OpenAI"
    />
  );
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <a className={`brand ${compact ? 'brand--compact' : ''}`} href="/" aria-label="SalesAI">
      <span className="brand__mark">
        <Sparkles size={19} strokeWidth={2.4} />
      </span>
      {!compact && (
        <span>
          Sales<span>AI</span>
        </span>
      )}
    </a>
  );
}

function AccountMenu({ session, onLoggedOut }: { session: Session; onLoggedOut: () => void }) {
  return (
    <FanLeadAccountMenuReact
      loading={false}
      authenticated={Boolean(session.user)}
      displayName={session.user?.displayName}
      avatarEmoji={(session.user?.avatarEmoji ?? null) as FanLeadAvatarEmoji | null}
      avatarUrl={session.user?.avatarUrl}
      preferEmoji={!session.user?.avatarUrl}
      menuItems={session.accountMenu?.items}
      loginUrl="/api/auth/sso/start?return_path=/app"
      logoutUrl="/api/auth/logout"
      onLogoutRequest={(_detail, event) => {
        event.preventDefault();
        void api('/api/auth/logout', { method: 'POST' }, session.csrfToken).then(onLoggedOut);
      }}
    />
  );
}

function Landing({ session, onLoggedOut }: { session: Session; onLoggedOut: () => void }) {
  return (
    <div className="landing">
      <header className="landing__header">
        <Brand />
        <nav aria-label="Основная навигация">
          <a href="#how">Как работает</a>
          <a href="#control">Контроль</a>
          <a href="#security">Безопасность</a>
        </nav>
        <div className="landing__actions">
          <AccountMenu session={session} onLoggedOut={onLoggedOut} />
          {!session.user && (
            <a className="button button--dark" href="/api/auth/sso/start?return_path=/app">
              Войти через FanLead ID
            </a>
          )}
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero__glow" />
          <div className="eyebrow">
            <span /> Один центр диалогов для VK и Telegram
          </div>
          <h1>
            Продажи продолжаются,
            <br /> даже когда команда <em>не в сети.</em>
          </h1>
          <p>
            SalesAI отвечает по инструкции AI-агента, помнит контекст и вовремя передаёт диалог менеджеру. Вся переписка
            — живая, прозрачная и под вашим контролем.
          </p>
          <div className="hero__actions">
            <a
              className="button button--lime button--large"
              href={session.user ? '/app' : '/api/auth/sso/start?return_path=/app'}
            >
              {session.user ? 'Открыть кабинет' : 'Подключить первый канал'} <ArrowRight size={18} />
            </a>
            <a className="button button--ghost button--large" href="#how">
              Посмотреть, как работает
            </a>
          </div>
          <div className="hero__proof">
            <span>
              <Check size={15} /> Токены зашифрованы
            </span>
            <span>
              <Check size={15} /> Ручной перехват
            </span>
            <span>
              <Check size={15} /> Полная история
            </span>
          </div>

          <div className="product-shot" aria-label="Пример интерфейса SalesAI">
            <div className="shot-sidebar">
              <Brand compact />
              <span className="shot-dot active" />
              <span className="shot-dot" />
              <span className="shot-dot" />
            </div>
            <div className="shot-list">
              <div className="shot-list__head">
                <span>Диалоги</span>
                <span className="tiny-pill">12 новых</span>
              </div>
              {['Анна Морозова', 'Михаил С.', 'Елена В.'].map((name, index) => (
                <div className={`shot-person ${index === 0 ? 'active' : ''}`} key={name}>
                  <span className={`avatar avatar--${index + 1}`}>{name[0]}</span>
                  <span>
                    <b>{name}</b>
                    <small>{index === 0 ? 'Да, четверг подойдёт…' : 'Можно уточнить стоимость?'}</small>
                  </span>
                  {index === 0 && <i>2</i>}
                </div>
              ))}
            </div>
            <div className="shot-chat">
              <div className="shot-chat__head">
                <span className="avatar avatar--1">А</span>
                <span>
                  <b>Анна Морозова</b>
                  <small>
                    <i /> VK · Кухни Мечты
                  </small>
                </span>
                <span className="bot-chip">
                  <Bot size={13} /> AI отвечает
                </span>
              </div>
              <div className="shot-chat__body">
                <div className="bubble bubble--in">Нужна угловая кухня, светлая. Бюджет примерно до 450 тысяч.</div>
                <div className="bubble bubble--out">
                  Понял! В этот бюджет можно собрать несколько вариантов. Предложить бесплатный замер?
                </div>
                <div className="bubble bubble--in">Да, в четверг после 18:00 будет удобно</div>
                <div className="typing">
                  <span />
                  <span />
                  <span /> SalesAI формирует ответ
                </div>
              </div>
            </div>
            <div className="shot-insight">
              <span className="insight-icon">
                <WandSparkles size={18} />
              </span>
              <small>AI определил</small>
              <b>Квалифицированный лид</b>
              <div>
                <span>Бюджет</span>
                <strong>до 450 000 ₽</strong>
              </div>
              <div>
                <span>Следующий шаг</span>
                <strong>Замер</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="feature-strip" id="how">
          <article>
            <span>
              <Inbox />
            </span>
            <b>Все каналы рядом</b>
            <p>VK и Telegram в одном аккуратном окне.</p>
          </article>
          <article>
            <span>
              <Bot />
            </span>
            <b>AI-агент под проект</b>
            <p>Версии, модели и правила ответа без кода.</p>
          </article>
          <article id="control">
            <span>
              <Users />
            </span>
            <b>Человек в любой момент</b>
            <p>Остановите бота и продолжите разговор сами.</p>
          </article>
          <article id="security">
            <span>
              <ShieldCheck />
            </span>
            <b>Память без хаоса</b>
            <p>Полная история локально, контекст — экономно.</p>
          </article>
        </section>
      </main>
    </div>
  );
}

function Sidebar({
  route,
  projectSlug,
  projects,
  projectId,
  onProjectChange,
  dialogCount,
  navigate,
  mobileOpen,
  close,
}: {
  route: CabinetRoute;
  projectSlug?: string;
  projects: Project[];
  projectId?: string;
  onProjectChange: (projectId: string) => void;
  dialogCount: number;
  navigate: (path: string) => void;
  mobileOpen: boolean;
  close: () => void;
}) {
  const selectedProject = projects.find((item) => item.id === projectId);
  const items: Array<[Tab, string, typeof MessageCircle]> = [
    ['dialogs', 'Диалоги', MessageCircle],
    ['analytics', 'Аналитика продаж', BarChart3],
    ['projects', 'Проекты', LayoutDashboard],
    ...(selectedProject?.role === 'admin'
      ? ([['settings', 'Настройки', Settings]] as Array<[Tab, string, typeof MessageCircle]>)
      : []),
  ];
  return (
    <aside className={`sidebar ${mobileOpen ? 'sidebar--open' : ''}`}>
      <div className="sidebar__top">
        <Brand />
        <button className="icon-button sidebar__close" onClick={close}>
          <X />
        </button>
      </div>
      <nav>
        <div className="sidebar-project-select">
          <HeaderSelect
            label="Проект"
            value={projectId ?? ''}
            icon={<Sparkles size={17} />}
            options={projects.map((project) => ({
              value: project.id,
              label: project.name,
              detail: `${project.sourceProjectName ?? project.slug} · ${formatDialogCount(project.conversationCount)}`,
            }))}
            onChange={onProjectChange}
          />
        </div>
        {items.map(([value, label, Icon]) => {
          const href =
            value === 'projects' ? '/app/projects' : projectSlug ? projectPath(projectSlug, value) : '/app/projects';
          return (
            <a
              key={value}
              href={href}
              className={route.tab === value ? 'active' : ''}
              onClick={(event) => {
                event.preventDefault();
                navigate(href);
                close();
              }}
            >
              <Icon size={19} /> {label}
              {value === 'dialogs' && <i>{dialogCount}</i>}
            </a>
          );
        })}
      </nav>
      <div className="sidebar__status">
        <span>
          <Radio size={14} /> Система работает
        </span>
        <small>API и worker на связи</small>
      </div>
    </aside>
  );
}

function StatCard({
  label,
  value,
  detail,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string;
  detail: string;
  icon: typeof Activity;
  accent: string;
}) {
  return (
    <article className="stat-card">
      <span className={`stat-card__icon ${accent}`}>
        <Icon size={20} />
      </span>
      <small>{label}</small>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

interface AnalyticsData {
  days: number;
  channelId: string | null;
  totals: { conversations: number; messages: number; qualified: number; estimatedCost: string };
  series: Array<{ date: string; conversations: number; qualified: number }>;
}

function mergeAnalytics(results: AnalyticsData[], days: number): AnalyticsData {
  const series = new Map<string, { date: string; conversations: number; qualified: number }>();
  let conversations = 0;
  let messages = 0;
  let qualified = 0;
  let estimatedCost = 0;
  for (const result of results) {
    conversations += result.totals.conversations;
    messages += result.totals.messages;
    qualified += result.totals.qualified;
    estimatedCost += Number(result.totals.estimatedCost);
    for (const point of result.series) {
      const current = series.get(point.date) ?? { date: point.date, conversations: 0, qualified: 0 };
      current.conversations += point.conversations;
      current.qualified += point.qualified;
      series.set(point.date, current);
    }
  }
  return {
    days,
    channelId: null,
    totals: { conversations, messages, qualified, estimatedCost: String(estimatedCost) },
    series: Array.from(series.values()).sort((left, right) => left.date.localeCompare(right.date)),
  };
}

function Analytics({ overview, project, projects }: { overview: Overview; project: Project; projects: Project[] }) {
  const [days, setDays] = useState(30);
  const [channelId, setChannelId] = useState('all');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([project.id]);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const currentProjectOnly = selectedProjectIds.length === 1 && selectedProjectIds[0] === project.id;
  useEffect(() => {
    setSelectedProjectIds([project.id]);
    setChannelId('all');
  }, [project.id]);
  useEffect(() => {
    if (!currentProjectOnly && channelId !== 'all') setChannelId('all');
  }, [currentProjectOnly, channelId]);
  useEffect(() => {
    let cancelled = false;
    setAnalyticsLoading(true);
    setAnalyticsError('');
    const requests = selectedProjectIds.map((projectId) => {
      const params = new URLSearchParams({ days: String(days) });
      if (currentProjectOnly && channelId !== 'all') params.set('channelId', channelId);
      return api<AnalyticsData>(`/api/projects/${projectId}/analytics?${params}`);
    });
    void Promise.all(requests)
      .then((results) => {
        if (!cancelled) setAnalytics(mergeAnalytics(results, days));
      })
      .catch(() => {
        if (!cancelled) setAnalyticsError('Не удалось загрузить аналитику выбранных проектов');
      })
      .finally(() => {
        if (!cancelled) setAnalyticsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [project.id, selectedProjectIds.join(','), currentProjectOnly, days, channelId]);
  const summary = analytics?.totals ?? {
    conversations: overview.summary.conversations ?? 0,
    messages: overview.summary.messages ?? 0,
    qualified: overview.summary.qualified ?? 0,
    estimatedCost: overview.summary.estimated_cost ?? '0',
  };
  const qualifiedRate = summary.conversations ? Math.round((summary.qualified / summary.conversations) * 100) : 0;
  const maximum = Math.max(1, ...(analytics?.series.map((point) => point.conversations) ?? [1]));
  const projectDetail =
    selectedProjectIds.length === 1
      ? projects.find((item) => item.id === selectedProjectIds[0])?.name
      : formatProjectCount(selectedProjectIds.length);
  return (
    <div className="page-stack">
      <section className="stats-grid">
        <StatCard
          label="Диалогов"
          value={String(summary.conversations)}
          detail={`${projectDetail ?? 'Проект'} · ${days} дней`}
          icon={MessageCircle}
          accent="mint"
        />
        <StatCard
          label="Сообщений"
          value={String(summary.messages)}
          detail="Входящие и исходящие"
          icon={Send}
          accent="sky"
        />
        <StatCard
          label="Квалифицировано"
          value={`${qualifiedRate}%`}
          detail={`${summary.qualified} целевых лидов`}
          icon={Zap}
          accent="yellow"
        />
        <StatCard
          label="Оценка AI"
          value={`$${Number(summary.estimatedCost).toFixed(2)}`}
          detail="По автоматическим тарифам"
          icon={CircleDollarSign}
          accent="violet"
        />
      </section>
      <section className="panel chart-panel">
        <div className="panel__head">
          <div>
            <h2>Динамика диалогов</h2>
            <p>Новые обращения и квалифицированные лиды</p>
          </div>
          <div className="analytics-filters">
            <ProjectMultiSelect
              projects={projects}
              selectedIds={selectedProjectIds}
              currentProjectId={project.id}
              onChange={setSelectedProjectIds}
            />
            <CompactSelect
              label="Период"
              value={String(days)}
              options={[
                { value: '7', label: '7 дней' },
                { value: '30', label: '30 дней' },
                { value: '90', label: '90 дней' },
                { value: '365', label: '365 дней' },
              ]}
              onChange={(value) => setDays(Number(value))}
            />
            {currentProjectOnly ? (
              <CompactSelect
                label="Канал"
                value={channelId}
                options={[
                  { value: 'all', label: 'Все каналы', detail: 'Сводная аналитика' },
                  ...overview.channels.map((channel) => ({
                    value: channel.id,
                    label: channel.customName || channel.name,
                    detail: channel.provider === 'vk' ? 'ВКонтакте' : 'Telegram',
                  })),
                ]}
                onChange={setChannelId}
              />
            ) : (
              <span className="compact-select compact-select--static">
                <span>Канал</span>
                <b>Все каналы</b>
              </span>
            )}
          </div>
        </div>
        <div className="chart">
          <div className="chart__grid" />
          <div className="chart__columns">
            {(analytics?.series ?? []).map((point) => {
              const dateLabel = new Date(`${point.date}T00:00:00`).toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              });
              return (
                <div
                  className="chart__column"
                  key={point.date}
                  tabIndex={0}
                  aria-label={`${dateLabel}: ${point.conversations} диалогов, ${point.qualified} квалифицировано`}
                >
                  <i
                    className="chart__bar chart__bar--all"
                    style={{ height: `${(point.conversations / maximum) * 100}%` }}
                  />
                  <i
                    className="chart__bar chart__bar--qualified"
                    style={{ height: `${(point.qualified / maximum) * 100}%` }}
                  />
                  <span className="chart__tooltip">
                    <b>{dateLabel}</b>
                    <small>За выбранный день</small>
                    <span>
                      <i className="legend-all" /> Новые диалоги <strong>{point.conversations}</strong>
                    </span>
                    <span>
                      <i className="legend-qualified" /> Квалифицированные <strong>{point.qualified}</strong>
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
          {analyticsLoading && <span className="chart__empty">Собираем данные по проектам…</span>}
          {analyticsError && <span className="chart__empty chart__empty--error">{analyticsError}</span>}
          {!analyticsLoading &&
            !analyticsError &&
            analytics &&
            !analytics.series.some((point) => point.conversations > 0) && (
              <span className="chart__empty">За выбранный период диалогов нет</span>
            )}
        </div>
        <div className="chart__legend">
          <span>
            <i className="legend-all" /> Новые диалоги
          </span>
          <span>
            <i className="legend-qualified" /> Квалифицированные
          </span>
        </div>
      </section>
    </div>
  );
}

function ThemeToggle({
  preference,
  onChange,
}: {
  preference: ThemePreference;
  onChange: (value: ThemePreference) => void;
}) {
  const options: Array<{ value: ThemePreference; label: string; icon: ReactNode }> = [
    { value: 'system', label: 'Как в системе', icon: <Monitor size={17} /> },
    { value: 'light', label: 'Светлая тема', icon: <Sun size={17} /> },
    { value: 'dark', label: 'Тёмная тема', icon: <Moon size={17} /> },
  ];
  const current = options.find((option) => option.value === preference) ?? options[0];
  return (
    <button
      type="button"
      className="icon-button theme-toggle"
      title={current?.label}
      aria-label={`${current?.label}. Нажмите, чтобы сменить тему`}
      onClick={() => {
        const index = options.findIndex((option) => option.value === preference);
        onChange(options[(index + 1) % options.length]?.value ?? 'system');
      }}
    >
      {current?.icon}
    </button>
  );
}

function Dialogs({
  project,
  overview,
  csrf,
  channelId,
  conversations,
  messages,
  messagesLoading,
  messagesError,
  selectedId,
  onSelect,
  onSearchSelect,
  onRefresh,
  onRetryMessages,
  onClose,
  typing,
  onMode,
  modeBusy,
  onResetAiSession,
  onSend,
  hasOlder,
  loadingOlder,
  onLoadOlder,
}: {
  project: Project;
  overview: Overview;
  csrf: string | null;
  channelId?: string;
  conversations: Conversation[];
  messages: ChatMessage[];
  messagesLoading: boolean;
  messagesError: string;
  selectedId?: string;
  onSelect: (id: string) => void;
  onSearchSelect: (result: ConversationSearchResult) => void;
  onRefresh: () => void;
  onRetryMessages: () => void;
  onClose: () => void;
  typing: boolean;
  onMode: (mode: 'bot' | 'manual') => void;
  modeBusy: boolean;
  onResetAiSession: () => void;
  onSend: (text: string) => void;
  hasOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => Promise<void>;
}) {
  const [draft, setDraft] = useState('');
  const [listFilter, setListFilter] = useState<'all' | 'manual' | 'automatic'>('all');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchScope, setSearchScope] = useState<'conversations' | 'messages'>('conversations');
  const [searchResults, setSearchResults] = useState<ConversationSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [notes, setNotes] = useState<ConversationNote[]>([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [detailError, setDetailError] = useState('');
  const chatBody = useRef<HTMLDivElement>(null);
  const initializedConversation = useRef<string | undefined>(undefined);
  const loadingOlderRef = useRef(false);
  const selected = selectedId ? conversations.find((item) => item.id === selectedId) : undefined;
  const selectedTitle = selected ? displayConversationTitle(selected) : '';
  const botActive = Boolean(selected?.botConfigured && selected.mode === 'bot');
  const manualReplyAllowed = Boolean(
    selected && !selected.blocked && (!selected.botConfigured || selected.mode === 'manual'),
  );
  const filteredConversations = conversations.filter((conversation) => {
    if (listFilter === 'manual') return conversation.mode === 'manual' || !conversation.botConfigured;
    if (listFilter === 'automatic') return conversation.mode === 'bot' && conversation.botConfigured;
    return true;
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (draft.trim()) {
      onSend(draft.trim());
      setDraft('');
    }
  };
  useEffect(() => {
    initializedConversation.current = undefined;
  }, [selectedId]);
  useEffect(() => {
    if (!searchOpen || !searchQuery.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      setSearchLoading(true);
      const query = new URLSearchParams({ q: searchQuery.trim(), scope: searchScope });
      if (channelId) query.set('channelId', channelId);
      void api<{ results: ConversationSearchResult[] }>(`/api/projects/${project.id}/search?${query}`, {
        signal: controller.signal,
      })
        .then((response) => setSearchResults(response.results))
        .catch((error: unknown) => {
          if (!(error instanceof DOMException && error.name === 'AbortError')) setSearchResults([]);
        })
        .finally(() => setSearchLoading(false));
    }, 250);
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [searchOpen, searchQuery, searchScope, project.id, channelId]);
  useEffect(() => {
    if (!selected) {
      setNotes([]);
      return;
    }
    setDetailError('');
    void api<{ notes: ConversationNote[] }>(`/api/projects/${project.id}/conversations/${selected.id}/notes`)
      .then((response) => setNotes(response.notes))
      .catch((error: unknown) => {
        setNotes([]);
        if (!demoMode) setDetailError(error instanceof Error ? error.message : 'Не удалось загрузить комментарии');
      });
  }, [project.id, selected?.id]);
  useEffect(() => {
    if (!selectedId || !messages.length || initializedConversation.current === selectedId) return;
    initializedConversation.current = selectedId;
    requestAnimationFrame(() => {
      const element = chatBody.current;
      if (element) element.scrollTop = element.scrollHeight;
    });
  }, [selectedId, messages.length]);
  const loadOlderAtTop = async () => {
    const element = chatBody.current;
    if (!element || element.scrollTop > 60 || !hasOlder || loadingOlder || loadingOlderRef.current) return;
    loadingOlderRef.current = true;
    const previousHeight = element.scrollHeight;
    try {
      await onLoadOlder();
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const current = chatBody.current;
          if (current) current.scrollTop = current.scrollHeight - previousHeight;
          loadingOlderRef.current = false;
        }),
      );
    } catch {
      loadingOlderRef.current = false;
    }
  };
  return (
    <div className="dialogs-layout">
      <section className="conversation-list">
        {searchOpen ? (
          <div className="conversation-search">
            <div className="conversation-search__head">
              <button
                type="button"
                className="icon-button"
                aria-label="Назад к диалогам"
                onClick={() => {
                  setSearchOpen(false);
                  setSearchQuery('');
                }}
              >
                <ArrowLeft size={19} />
              </button>
              <label>
                <Search size={17} />
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={searchScope === 'messages' ? 'Текст сообщения' : 'Имя собеседника'}
                />
                {searchQuery && (
                  <button type="button" aria-label="Очистить поиск" onClick={() => setSearchQuery('')}>
                    <X size={16} />
                  </button>
                )}
              </label>
            </div>
            <div className="conversation-search__tabs">
              <button
                type="button"
                className={searchScope === 'conversations' ? 'active' : ''}
                onClick={() => setSearchScope('conversations')}
              >
                Диалоги
              </button>
              <button
                type="button"
                className={searchScope === 'messages' ? 'active' : ''}
                onClick={() => setSearchScope('messages')}
              >
                Сообщения
              </button>
            </div>
            <div className="conversation-search__results">
              {!searchQuery.trim() ? (
                <div className="search-placeholder">
                  <Search size={42} />
                  <span>{searchScope === 'messages' ? 'Введите текст сообщения' : 'Введите имя собеседника'}</span>
                </div>
              ) : searchLoading ? (
                <div className="search-placeholder">
                  <span className="loader" />
                </div>
              ) : searchResults.length ? (
                searchResults.map((result, index) => (
                  <button
                    type="button"
                    key={`${result.conversationId}-${result.createdAt}-${index}`}
                    onClick={() => {
                      setSearchOpen(false);
                      setSearchQuery('');
                      onSearchSelect(result);
                    }}
                  >
                    <span className="avatar">
                      {result.avatarUrl ? <img src={result.avatarUrl} alt="" /> : result.title.charAt(0)}
                    </span>
                    <span>
                      <b>{result.title}</b>
                      <small>{result.snippet || result.channelName}</small>
                    </span>
                    <time>{relativeTime(result.createdAt)}</time>
                  </button>
                ))
              ) : (
                <div className="search-placeholder">
                  <span>Ничего не найдено</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="filters">
              <button className={listFilter === 'all' ? 'active' : ''} onClick={() => setListFilter('all')}>
                Все <i>{conversations.length}</i>
              </button>
              <button className={listFilter === 'manual' ? 'active' : ''} onClick={() => setListFilter('manual')}>
                Ручные
              </button>
              <button className={listFilter === 'automatic' ? 'active' : ''} onClick={() => setListFilter('automatic')}>
                Автоматические
              </button>
              <button className="filters__search" aria-label="Поиск" onClick={() => setSearchOpen(true)}>
                <Search size={18} />
              </button>
            </div>
            <div className="dialogs-cost-summary" title="Сумма расходов всех AI-вызовов в доступных диалогах">
              <span>Стоимость всех диалогов</span>
              <b>{formatAiCost(overview.summary.estimated_cost)}</b>
            </div>
            <div className="conversation-scroll">
              {filteredConversations.map((conversation, index) => (
                <button
                  className={`conversation-row ${conversation.id === selected?.id ? 'active' : ''}`}
                  key={conversation.id}
                  onClick={() => onSelect(conversation.id)}
                >
                  <ContactAvatar conversation={conversation} className={`avatar avatar--${(index % 3) + 1}`} />
                  <span className="conversation-row__body">
                    <span>
                      <b>{displayConversationTitle(conversation)}</b>
                      <time>{relativeTime(conversation.lastMessageAt)}</time>
                    </span>
                    <small>
                      {conversation.blocked ? 'Диалог заблокирован пользователем' : conversation.lastMessage}
                    </small>
                    <span className="conversation-row__meta">
                      <em className={conversation.provider}>
                        {conversation.provider === 'vk' ? 'VK' : 'TG'} ·{' '}
                        {conversation.channelName.replace(/^.+ · /, '')}
                      </em>
                      {conversation.labelIds.slice(0, 2).map((labelId) => {
                        const label = overview.labels.find((item) => item.id === labelId);
                        return label ? (
                          <i key={label.id} style={{ '--label-color': label.color } as CSSProperties}>
                            {label.name}
                          </i>
                        ) : null;
                      })}
                    </span>
                  </span>
                  {conversation.unread ? <i>{conversation.unread}</i> : null}
                </button>
              ))}
              {!filteredConversations.length && <div className="empty-inline">В этой группе пока нет диалогов.</div>}
            </div>
          </>
        )}
      </section>
      {selected ? (
        <section className="chat-panel">
          <header className="chat-header">
            <button className="icon-button chat-back" onClick={onClose}>
              <ArrowLeft size={18} />
            </button>
            <div className="chat-person">
              <ContactAvatar conversation={selected} className="avatar avatar--1" />
              <span>
                <b>{selectedTitle}</b>
                <small>
                  <i /> {selected.channelName}
                </small>
              </span>
            </div>
            <div className="chat-actions">
              <span className="conversation-cost" title="Расходы всех AI-сессий этого диалога">
                {selected.costCalculated ? formatAiCost(selected.estimatedCost) : 'Стоимость: —'}
              </span>
              <span className={`qualification qualification--${selected.qualification}`}>
                {selected.qualification === 'qualified'
                  ? 'Квал'
                  : selected.qualification === 'unqualified'
                    ? 'Не квал'
                    : 'Не определён'}
              </span>
              <button
                className={`button mode-button ${botActive ? 'button--soft' : 'button--warning'}`}
                onClick={() => onMode(selected.mode === 'bot' ? 'manual' : 'bot')}
                disabled={!selected.botConfigured || modeBusy}
              >
                {!selected.botConfigured ? (
                  <>
                    <Pause size={16} /> Только вручную
                  </>
                ) : modeBusy ? (
                  <>
                    <span className="loader" /> Сохраняем…
                  </>
                ) : botActive ? (
                  <>
                    <Pause size={16} /> Остановить AI
                  </>
                ) : (
                  <>
                    <Play size={16} /> Возобновить AI
                  </>
                )}
              </button>
            </div>
          </header>
          <div className="chat-body" ref={chatBody} onScroll={() => void loadOlderAtTop()}>
            {messagesLoading ? (
              <div className="conversation-loading" role="status">
                <span className="loader" />
                <span>Загружаем переписку…</span>
              </div>
            ) : messagesError ? (
              <div className="conversation-loading conversation-loading--error" role="alert">
                <span>{messagesError}</span>
                <button type="button" className="button button--secondary" onClick={onRetryMessages}>
                  Повторить
                </button>
              </div>
            ) : (
              <>
                {(hasOlder || loadingOlder) && (
                  <div className="history-loader">
                    {loadingOlder ? 'Загружаем предыдущие сообщения…' : 'Прокрутите вверх, чтобы загрузить историю'}
                  </div>
                )}
                <div className="day-divider">
                  <span>Сегодня</span>
                </div>
                {messages.map((message) => {
                  if (message.kind === 'technical') {
                    return (
                      <div className="technical-message" key={message.id} role="note">
                        <Activity size={14} aria-hidden="true" />
                        <span className="technical-message__text">{message.text}</span>
                        {message.usage && <MessageUsageBadge usage={message.usage} technical />}
                        <time dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
                      </div>
                    );
                  }
                  const mediaOnly = isMediaOnlyMessage(message);
                  const videoOnly = mediaOnly && message.attachments.every((attachment) => attachment.type === 'video');
                  return (
                    <div
                      className={`message ${message.direction === 'outbound' ? 'message--out' : 'message--in'} ${mediaOnly ? 'message--media-only' : ''}`}
                      key={message.id}
                    >
                      <div className={`message__bubble ${videoOnly ? 'message__bubble--video-only' : ''}`}>
                        <MessageAttachments attachments={message.attachments} />
                        {message.text && <div className="message__text">{message.text}</div>}
                        <span className="message__meta">
                          {message.usage && <MessageUsageBadge usage={message.usage} />}
                          {formatTime(message.createdAt)}{' '}
                          {message.direction === 'outbound' &&
                            (message.status === 'read' ? (
                              <CheckCheck
                                className="message-status message-status--read"
                                size={14}
                                aria-label="Прочитано"
                              />
                            ) : (
                              <Check className="message-status" size={13} aria-label="Отправлено" />
                            ))}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {typing && (
                  <div
                    className="typing typing--chat"
                    title="Статус набора текста передаётся собеседнику через API канала"
                  >
                    <span />
                    <span />
                    <span /> AI печатает в {selected.provider === 'vk' ? 'VK' : 'Telegram'}…
                  </div>
                )}
              </>
            )}
          </div>
          {selected.blocked && (
            <div className="dialog-blocked" role="status">
              Диалог заблокирован пользователем
            </div>
          )}
          <form className="composer" onSubmit={submit}>
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={
                messagesLoading
                  ? 'Загружаем переписку…'
                  : selected.blocked
                    ? 'Диалог заблокирован пользователем'
                    : manualReplyAllowed
                      ? 'Напишите сообщение…'
                      : 'Работает AI — остановите его, чтобы написать вручную'
              }
              disabled={messagesLoading || Boolean(messagesError) || !manualReplyAllowed}
              rows={1}
            />
            <button
              className="send-button"
              disabled={messagesLoading || Boolean(messagesError) || !manualReplyAllowed || !draft.trim()}
            >
              <Send size={18} />
            </button>
          </form>
        </section>
      ) : (
        <section className="empty-chat">
          <MessageCircle />
          <h2>Выберите диалог</h2>
        </section>
      )}
      <aside className="contact-panel">
        {selected && (
          <>
            <ContactAvatar conversation={selected} className="contact-avatar" />
            <h3>{selectedTitle}</h3>
            <span className="muted">Клиент из {selected.provider === 'vk' ? 'ВКонтакте' : 'Telegram'}</span>
            <div className="lead-card">
              <span>
                <WandSparkles size={16} /> AI-контекст
              </span>
              <dl>
                <div>
                  <dt>Статус</dt>
                  <dd>
                    {selected.automationOutcome === 'success'
                      ? 'Успех'
                      : selected.automationOutcome === 'negative'
                        ? 'Негатив'
                        : selected.automationOutcome === 'agreement'
                          ? 'Договорённость'
                          : selected.automationOutcome === 'manager'
                            ? 'Нужен менеджер'
                            : 'В работе'}
                  </dd>
                </div>
                <div>
                  <dt>Сообщений</dt>
                  <dd>{selected.messageCount}</dd>
                </div>
                <div>
                  <dt>Режим</dt>
                  <dd>{botActive ? 'Автоответ' : 'Менеджер'}</dd>
                </div>
                <div>
                  <dt>Стоимость диалога</dt>
                  <dd>{selected.costCalculated ? formatAiCost(selected.estimatedCost) : 'Не рассчитана'}</dd>
                </div>
                <div>
                  <dt>AI-сессии</dt>
                  <dd>{selected.aiSessionCount ?? 0}</dd>
                </div>
                {selected.latestAiSession && (
                  <div>
                    <dt>
                      Сессия {selected.latestAiSession.number} ·{' '}
                      {selected.latestAiSession.status === 'active' ? 'активна' : 'закрыта'}
                    </dt>
                    <dd>
                      {selected.latestAiSession.costCalculated
                        ? formatAiCost(selected.latestAiSession.estimatedCost)
                        : 'Не рассчитана'}
                    </dd>
                  </div>
                )}
              </dl>
              {botActive && (
                <button
                  type="button"
                  className="ai-session-reset"
                  onClick={onResetAiSession}
                  disabled={modeBusy}
                  title="Закрыть текущую AI-сессию и начать новую с актуальной перепиской"
                >
                  <RefreshCw size={14} /> Обновить AI-контекст
                </button>
              )}
            </div>
            {selected.automationOutcome !== 'active' && selected.automationOutcomeReason && (
              <div className={`outcome-status outcome-status--${selected.automationOutcome}`}>
                <span>
                  <Activity size={16} /> Результат анализа
                </span>
                <b>
                  {selected.automationOutcome === 'success'
                    ? 'Целевое действие выполнено'
                    : selected.automationOutcome === 'negative'
                      ? 'Диалог закрыт по негативу'
                      : selected.automationOutcome === 'manager'
                        ? 'Требуется участие менеджера'
                        : 'Точная договорённость'}
                </b>
                <small>{selected.automationOutcomeReason}</small>
              </div>
            )}
            {selected.followUp?.status === 'waiting' && (
              <div className="follow-up-status follow-up-status--waiting">
                <span>
                  <Zap size={16} /> Автоматизация
                </span>
                <b>Запущено ожидание дожима {selected.followUp.step}</b>
                <small>Отправка: {formatFollowUpDate(selected.followUp.nextRunAt)}</small>
              </div>
            )}
            {selected.followUp?.status === 'paused_task' && (
              <div className="follow-up-status follow-up-status--task">
                <span>
                  <Check size={16} /> Договорённость
                </span>
                <b>{selected.followUp.taskTitle}</b>
                <small>{formatFollowUpDate(selected.followUp.taskDueAt)} · дожимы отключены</small>
              </div>
            )}
            <div className="conversation-labels">
              <button type="button" onClick={() => setLabelsOpen((value) => !value)}>
                <span>
                  <Tags size={15} /> Метки
                </span>
                <Plus size={15} />
              </button>
              <div className="conversation-labels__chips">
                {selected.labelIds.map((labelId) => {
                  const label = overview.labels.find((item) => item.id === labelId);
                  return label ? (
                    <span key={label.id} style={{ '--label-color': label.color } as CSSProperties}>
                      {label.name}
                    </span>
                  ) : null;
                })}
                {!selected.labelIds.length && <small>Нет меток</small>}
              </div>
              {labelsOpen && (
                <div className="conversation-labels__menu">
                  {overview.labels.map((label) => (
                    <label key={label.id}>
                      <input
                        type="checkbox"
                        checked={selected.labelIds.includes(label.id)}
                        onChange={() => {
                          const labelIds = selected.labelIds.includes(label.id)
                            ? selected.labelIds.filter((id) => id !== label.id)
                            : [...selected.labelIds, label.id];
                          void api(
                            `/api/projects/${project.id}/conversations/${selected.id}/labels`,
                            { method: 'PUT', body: JSON.stringify({ labelIds }) },
                            csrf,
                          )
                            .then(() => {
                              setDetailError('');
                              onRefresh();
                            })
                            .catch((error: unknown) =>
                              setDetailError(error instanceof Error ? error.message : 'Не удалось сохранить метки'),
                            );
                        }}
                      />
                      <i style={{ background: label.color }} /> {label.name}
                    </label>
                  ))}
                  {!overview.labels.length && <small>Создайте метки в настройках проекта.</small>}
                </div>
              )}
            </div>
            <div className="conversation-notes">
              <h4>
                <MessageSquareText size={15} /> Комментарии команды
              </h4>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  const body = noteDraft.trim();
                  if (!body) return;
                  void api<ConversationNote>(
                    `/api/projects/${project.id}/conversations/${selected.id}/notes`,
                    { method: 'POST', body: JSON.stringify({ body }) },
                    csrf,
                  )
                    .then((note) => {
                      setNotes((items) => [note, ...items]);
                      setNoteDraft('');
                      setDetailError('');
                    })
                    .catch((error: unknown) =>
                      setDetailError(error instanceof Error ? error.message : 'Не удалось добавить комментарий'),
                    );
                }}
              >
                <textarea
                  rows={2}
                  maxLength={5000}
                  value={noteDraft}
                  onChange={(event) => setNoteDraft(event.target.value)}
                  placeholder="Внутренний комментарий…"
                />
                <button type="submit" disabled={!noteDraft.trim()}>
                  Добавить
                </button>
              </form>
              {detailError && <small className="conversation-detail-error">{detailError}</small>}
              <div className="conversation-notes__list">
                {notes.map((note) => (
                  <article key={note.id}>
                    <p>{note.body}</p>
                    <span>
                      {note.authorName} · {relativeTime(note.createdAt)}
                    </span>
                  </article>
                ))}
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function ContactAvatar({ conversation, className }: { conversation: Conversation; className: string }) {
  const title = displayConversationTitle(conversation);
  return (
    <span className={className}>
      {conversation.avatarUrl ? <img src={conversation.avatarUrl} alt="" loading="lazy" /> : title.charAt(0)}
    </span>
  );
}

function isMediaOnlyMessage(message: ChatMessage): boolean {
  return (
    !message.text.trim() &&
    message.attachments.length > 0 &&
    message.attachments.every((attachment) =>
      ['audio', 'voice', 'video', 'photo', 'gif', 'sticker'].includes(attachment.type),
    )
  );
}

function isVideoMessage(attachment: ChatMessage['attachments'][number]): boolean {
  const title = attachment.title?.trim().toLocaleLowerCase('ru-RU');
  return attachment.mimeType === 'video/message' || title === 'видеосообщение' || title === 'video_message';
}

const voiceBars = [
  6, 11, 17, 9, 22, 14, 8, 19, 25, 13, 7, 16, 21, 12, 27, 18, 9, 14, 23, 11, 6, 18, 25, 15, 9, 20, 13, 28, 17, 8, 22,
  14, 10, 25, 19, 7, 15, 23, 12, 18, 26, 9, 16, 21, 13, 7, 19, 24,
];

function VoicePlayer({ attachment }: { attachment: ChatMessage['attachments'][number] }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(attachment.durationSeconds ?? 0);
  const [rate, setRate] = useState(1);
  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      try {
        await audio.play();
      } catch {
        setPlaying(false);
      }
    } else {
      audio.pause();
    }
  };

  const seek = (event: MouseEvent<HTMLButtonElement>) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * audio.duration;
    setCurrentTime(audio.currentTime);
  };

  const cycleRate = () => {
    const nextRate = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
    setRate(nextRate);
    if (audioRef.current) audioRef.current.playbackRate = nextRate;
  };

  return (
    <div className="voice-player">
      <audio
        ref={audioRef}
        preload="metadata"
        src={attachment.url}
        onLoadedMetadata={(event) => {
          if (Number.isFinite(event.currentTarget.duration)) setDuration(event.currentTarget.duration);
          event.currentTarget.playbackRate = rate;
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrentTime(0);
        }}
      />
      <button
        className="voice-player__play"
        type="button"
        onClick={() => void togglePlayback()}
        aria-label={playing ? 'Пауза' : 'Воспроизвести'}
      >
        {playing ? <Pause size={16} fill="currentColor" /> : <Play size={17} fill="currentColor" />}
      </button>
      <button className="voice-player__wave" type="button" onClick={seek} aria-label="Перемотать голосовое сообщение">
        {voiceBars.map((height, index) => (
          <i
            className={index / voiceBars.length <= progress ? 'is-played' : ''}
            style={{ '--voice-bar-height': `${height}px` } as CSSProperties}
            key={`${attachment.id}-${index}`}
          />
        ))}
      </button>
      <span className="voice-player__time">{formatDuration(playing || currentTime > 0 ? currentTime : duration)}</span>
      <button
        className="voice-player__rate"
        type="button"
        onClick={cycleRate}
        aria-label="Изменить скорость воспроизведения"
      >
        {rate}×
      </button>
    </div>
  );
}

function VideoMessagePlayer({ attachment }: { attachment: ChatMessage['attachments'][number] }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const togglePlayback = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      try {
        await video.play();
      } catch {
        setPlaying(false);
      }
    } else {
      video.pause();
    }
  };

  return (
    <div className="video-message-player">
      <video
        ref={videoRef}
        playsInline
        preload="metadata"
        poster={attachment.previewUrl}
        src={attachment.url}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <button
        className={playing ? 'is-playing' : ''}
        type="button"
        onClick={() => void togglePlayback()}
        aria-label={playing ? 'Пауза' : 'Воспроизвести видеосообщение'}
      >
        {playing ? <Pause size={32} fill="currentColor" /> : <Play size={38} fill="currentColor" />}
      </button>
      {attachment.durationSeconds !== undefined && <small>{formatDuration(attachment.durationSeconds)}</small>}
    </div>
  );
}

function VideoMessagePreview({ attachment }: { attachment: ChatMessage['attachments'][number] }) {
  return (
    <a
      className="video-message-player video-message-player--preview"
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      aria-label="Открыть видеосообщение ВКонтакте"
    >
      {attachment.previewUrl && <img src={attachment.previewUrl} alt="" loading="lazy" />}
      <span>
        <Play size={38} fill="currentColor" />
      </span>
      {attachment.durationSeconds !== undefined && <small>{formatDuration(attachment.durationSeconds)}</small>}
    </a>
  );
}

function MessageAttachments({ attachments }: { attachments: ChatMessage['attachments'] }) {
  if (!attachments.length) return null;
  return (
    <div className="message-attachments">
      {attachments.map((attachment) => {
        const label = attachment.title || attachmentLabel(attachment.type);
        const stickerUrl =
          attachment.type === 'sticker' && !attachment.url && attachment.providerId
            ? `https://vk.com/sticker/1-${attachment.providerId}-512`
            : attachment.url;
        if ((attachment.type === 'photo' || attachment.type === 'gif' || attachment.type === 'sticker') && stickerUrl)
          return (
            <a
              className={`message-media message-media--${attachment.type}`}
              href={stickerUrl}
              target="_blank"
              rel="noreferrer"
              key={attachment.id}
            >
              <img src={stickerUrl} alt={label} loading="lazy" />
            </a>
          );
        if (attachment.type === 'voice' && attachment.url)
          return <VoicePlayer attachment={attachment} key={attachment.id} />;
        if (attachment.type === 'audio' && attachment.url)
          return (
            <div className="message-audio" key={attachment.id}>
              <Music size={17} />
              <div>
                <b>
                  {label}
                  {attachment.durationSeconds !== undefined && (
                    <small>{formatDuration(attachment.durationSeconds)}</small>
                  )}
                </b>
                <audio controls preload="metadata" src={attachment.url} />
              </div>
            </div>
          );
        if (attachment.type === 'video') {
          const embedUrl = vkVideoEmbedUrl(attachment);
          const videoMessage = isVideoMessage(attachment);
          if (videoMessage && attachment.url && /\.(mp4|webm)(?:\?|$)/i.test(attachment.url))
            return <VideoMessagePlayer attachment={attachment} key={attachment.id} />;
          if (videoMessage) return <VideoMessagePreview attachment={attachment} key={attachment.id} />;
          if (attachment.url && /\.(mp4|webm)(?:\?|$)/i.test(attachment.url))
            return (
              <video
                className="message-video-player"
                controls
                preload="metadata"
                poster={attachment.previewUrl}
                src={attachment.url}
                key={attachment.id}
              />
            );
          if (embedUrl)
            return (
              <div className="message-video-embed" key={attachment.id}>
                <iframe
                  src={embedUrl}
                  title={label}
                  allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            );
          return (
            <a className="message-video" href={attachment.url} target="_blank" rel="noreferrer" key={attachment.id}>
              {attachment.previewUrl && <img src={attachment.previewUrl} alt="" loading="lazy" />}
              <span>
                <PlayCircle size={24} /> {label}
              </span>
            </a>
          );
        }
        if (attachment.type === 'other' && attachment.title === 'video_message') {
          const url = attachment.providerId ? `https://vk.com/video${attachment.providerId}` : undefined;
          return <VideoMessagePreview attachment={{ ...attachment, url }} key={attachment.id} />;
        }
        if (attachment.url)
          return (
            <a className="message-file" href={attachment.url} target="_blank" rel="noreferrer" key={attachment.id}>
              <FileText size={18} /> <span>{label}</span>
            </a>
          );
        return (
          <div className="message-file" key={attachment.id}>
            <FileText size={18} /> <span>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const safe = Math.max(0, Math.round(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
}

function vkVideoEmbedUrl(attachment: ChatMessage['attachments'][number]): string | undefined {
  if (!attachment.providerId || !/^-?\d+_\d+$/.test(attachment.providerId)) return undefined;
  const [ownerId, videoId] = attachment.providerId.split('_');
  if (!ownerId || !videoId) return undefined;
  const accessKey = attachment.url?.match(new RegExp(`${attachment.providerId}_([A-Za-z0-9_-]+)$`))?.[1];
  const query = new URLSearchParams({ oid: ownerId, id: videoId });
  if (accessKey) query.set('hash', accessKey);
  return `https://vk.com/video_ext.php?${query.toString()}`;
}

function attachmentLabel(type: ChatMessage['attachments'][number]['type']) {
  return {
    photo: 'Фотография',
    video: 'Видео',
    audio: 'Аудиозапись',
    voice: 'Голосовое сообщение',
    document: 'Документ',
    gif: 'GIF',
    sticker: 'Стикер',
    link: 'Ссылка',
    other: 'Вложение',
  }[type];
}

function SettingsPage({
  project,
  overview,
  session,
  onRefresh,
  section,
  onSectionChange,
  notify,
}: {
  project: Project;
  overview: Overview;
  session: Session;
  onRefresh: () => void;
  section: SettingsSection;
  onSectionChange: (section: SettingsSection) => void;
  notify: Notify;
}) {
  const isAdmin = project.role === 'admin';
  const run: SettingsAction = async (work, successMessage = 'Сохранено') => {
    try {
      await work();
      notify(successMessage);
      onRefresh();
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : 'Ошибка', 'error');
    }
  };
  return (
    <div className="page-stack settings-page">
      {!isAdmin && (
        <div className="notice notice--info">
          <ShieldCheck size={18} /> У менеджера доступ только к диалогам и аналитике.
        </div>
      )}
      <div className="settings-layout">
        <nav className="settings-nav">
          <a
            href={projectPath(project.slug, 'settings', 'channels')}
            className={section === 'channels' ? 'active' : ''}
            onClick={(event) => {
              event.preventDefault();
              onSectionChange('channels');
            }}
          >
            <Radio /> Каналы
          </a>
          <a
            href={projectPath(project.slug, 'settings', 'access')}
            className={section === 'access' ? 'active' : ''}
            onClick={(event) => {
              event.preventDefault();
              onSectionChange('access');
            }}
          >
            <Users /> Доступы
          </a>
          <a
            href={projectPath(project.slug, 'settings', 'prompts')}
            className={section === 'prompts' ? 'active' : ''}
            onClick={(event) => {
              event.preventDefault();
              onSectionChange('prompts');
            }}
          >
            <WandSparkles /> AI-агенты продаж
          </a>
          <a
            href={projectPath(project.slug, 'settings', 'comments')}
            className={section === 'comments' ? 'active' : ''}
            onClick={(event) => {
              event.preventDefault();
              onSectionChange('comments');
            }}
          >
            <MessageSquareText /> AI-комментарии
          </a>
          <a
            href={projectPath(project.slug, 'settings', 'integrations')}
            className={section === 'integrations' ? 'active' : ''}
            onClick={(event) => {
              event.preventDefault();
              onSectionChange('integrations');
            }}
          >
            <Zap /> Интеграции
          </a>
          <a
            href={projectPath(project.slug, 'settings', 'labels')}
            className={section === 'labels' ? 'active' : ''}
            onClick={(event) => {
              event.preventDefault();
              onSectionChange('labels');
            }}
          >
            <Tags /> Метки диалогов
          </a>
          <a
            href={projectPath(project.slug, 'settings', 'openai')}
            className={section === 'openai' ? 'active' : ''}
            onClick={(event) => {
              event.preventDefault();
              onSectionChange('openai');
            }}
          >
            <KeyRound /> OpenAI и стоимость
          </a>
        </nav>
        <section className="panel settings-panel">
          {section === 'channels' && (
            <ChannelsSettings
              project={project}
              overview={overview}
              disabled={!isAdmin}
              onSave={run}
              csrf={session.csrfToken}
            />
          )}
          {section === 'access' && (
            <AccessSettings project={project} overview={overview} csrf={session.csrfToken} notify={notify} />
          )}
          {section === 'prompts' && (
            <PromptSettings
              project={project}
              overview={overview}
              disabled={!isAdmin}
              onSave={run}
              csrf={session.csrfToken}
            />
          )}
          {section === 'comments' && (
            <CommentSettings
              project={project}
              overview={overview}
              disabled={!isAdmin}
              onSave={run}
              csrf={session.csrfToken}
            />
          )}
          {section === 'integrations' && (
            <IntegrationSettings project={project} csrf={session.csrfToken} disabled={!isAdmin} notify={notify} />
          )}
          {section === 'labels' && (
            <LabelSettings
              project={project}
              overview={overview}
              disabled={!isAdmin}
              onSave={run}
              csrf={session.csrfToken}
            />
          )}
          {section === 'openai' && (
            <AiSettings
              project={project}
              overview={overview}
              disabled={!isAdmin}
              onSave={run}
              csrf={session.csrfToken}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function AccessSettings({
  project,
  overview,
  csrf,
  notify,
}: {
  project: Project;
  overview: Overview;
  csrf: string | null;
  notify: Notify;
}) {
  const [entries, setEntries] = useState<ProjectAccessEntry[]>([]);
  const [identifierType, setIdentifierType] = useState<'fanlead' | 'vk'>('fanlead');
  const [identifier, setIdentifier] = useState('');
  const [lookup, setLookup] = useState<{
    registered: boolean;
    user: { displayName: string; fanleadId: string; avatarUrl?: string; vkUserId?: number } | null;
  } | null>(null);
  const [role, setRole] = useState<'admin' | 'manager'>('manager');
  const [allProjects, setAllProjects] = useState(false);
  const [allChannels, setAllChannels] = useState(true);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [inviteLink, setInviteLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (demoMode) {
      setEntries([
        {
          id: '44444444-4444-4444-8444-444444444444',
          kind: 'member',
          displayName: 'Анна Соколова',
          identifier: '482917305',
          identifierType: 'fanlead',
          role: 'manager',
          channelIds: ['c1', 'c2'],
          allProjects: false,
          status: 'active',
        },
      ]);
      return;
    }
    void api<{ entries: ProjectAccessEntry[] }>(`/api/projects/${project.id}/access`)
      .then((result) => {
        if (!cancelled) setEntries(result.entries);
      })
      .catch((caught: Error) => {
        if (!cancelled) notify(caught.message, 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [project.id, reloadVersion]);

  const normalizedIdentifier = identifier.replace(/\D/g, '');
  const canSearch =
    identifierType === 'fanlead'
      ? normalizedIdentifier.length === 9
      : normalizedIdentifier.length > 0 && normalizedIdentifier.length <= 20;
  const effectiveAllChannels = role === 'admin' || allProjects || allChannels;

  const searchUser = async () => {
    setInviteLink('');
    setLoading(true);
    try {
      if (demoMode) {
        setLookup(
          normalizedIdentifier === '482917305'
            ? { registered: true, user: { displayName: 'Анна Соколова', fanleadId: '482917305' } }
            : { registered: false, user: null },
        );
        return;
      }
      const params = new URLSearchParams({ identifierType, identifier: normalizedIdentifier });
      setLookup(await api(`/api/projects/${project.id}/access/lookup?${params}`));
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : 'Не удалось выполнить поиск', 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveAccess = async () => {
    if (!lookup) return;
    if (!effectiveAllChannels && selectedChannels.length === 0) {
      notify('Выберите хотя бы один канал', 'error');
      return;
    }
    setInviteLink('');
    setLoading(true);
    try {
      if (demoMode) {
        if (lookup.registered) notify('Доступ сохранён');
        else {
          setInviteLink(`${location.origin}/api/access-invitations/demo-invitation-token/accept`);
          notify('Приглашение создано');
        }
        return;
      }
      const result = await api<
        { status: 'granted'; user: { displayName: string } } | { status: 'invited'; inviteLink: string }
      >(
        `/api/projects/${project.id}/access`,
        {
          method: 'POST',
          body: JSON.stringify({
            identifierType,
            identifier: normalizedIdentifier,
            role,
            channelIds: effectiveAllChannels ? null : selectedChannels,
            allProjects,
          }),
        },
        csrf,
      );
      if (result.status === 'granted') notify(`Доступ для ${result.user.displayName} сохранён`);
      else {
        setInviteLink(result.inviteLink);
        notify('Приглашение создано');
      }
      setReloadVersion((value) => value + 1);
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : 'Не удалось сохранить доступ', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="access-settings">
      <header className="settings-section-head">
        <span className="settings-section-head__icon">
          <UserPlus size={20} />
        </span>
        <div>
          <h2>Доступ к проекту</h2>
          <p>Найдите человека по FanLead ID или подтверждённому VK ID и назначьте его роль.</p>
        </div>
      </header>

      <section className="access-invite-card">
        <div className="access-id-tabs" aria-label="Тип идентификатора">
          <button
            type="button"
            className={identifierType === 'fanlead' ? 'active' : ''}
            onClick={() => {
              setIdentifierType('fanlead');
              setLookup(null);
            }}
          >
            FanLead ID
          </button>
          <button
            type="button"
            className={identifierType === 'vk' ? 'active' : ''}
            onClick={() => {
              setIdentifierType('vk');
              setLookup(null);
            }}
          >
            VK ID
          </button>
        </div>
        <div className="access-search-row">
          <label>
            <span>{identifierType === 'fanlead' ? 'FanLead ID из 9 цифр' : 'Числовой ID ВКонтакте'}</span>
            <span className="access-search-input">
              <Hash size={17} />
              <input
                inputMode="numeric"
                value={identifier}
                placeholder={identifierType === 'fanlead' ? 'Например, 739184205' : 'Например, 123456789'}
                onChange={(event) => {
                  setIdentifier(event.target.value.replace(/\D/g, '').slice(0, identifierType === 'fanlead' ? 9 : 20));
                  setLookup(null);
                  setInviteLink('');
                }}
              />
            </span>
          </label>
          <button
            className="button button--outline"
            type="button"
            disabled={!canSearch || loading}
            onClick={searchUser}
          >
            <Search size={16} /> {loading ? 'Ищем…' : 'Найти'}
          </button>
        </div>

        {lookup && (
          <div className={`access-lookup ${lookup.registered ? 'access-lookup--found' : ''}`}>
            {lookup.registered && lookup.user ? (
              <>
                <span className="access-avatar">
                  {lookup.user.avatarUrl ? (
                    <img src={lookup.user.avatarUrl} alt="" />
                  ) : (
                    lookup.user.displayName.charAt(0)
                  )}
                </span>
                <span>
                  <b>{lookup.user.displayName}</b>
                  <small>Зарегистрирован в FanLead · ID {lookup.user.fanleadId}</small>
                </span>
                <Check size={18} />
              </>
            ) : (
              <>
                <UserPlus size={19} />
                <span>
                  <b>Пользователь ещё не зарегистрирован</b>
                  <small>Настройте права — после сохранения появится персональная ссылка-приглашение.</small>
                </span>
              </>
            )}
          </div>
        )}

        {lookup && (
          <div className="access-scope-grid">
            <div className="access-field">
              <span>Роль</span>
              <CustomSelect
                value={role}
                ariaLabel="Роль пользователя"
                options={[
                  { value: 'manager', label: 'Менеджер', detail: 'Диалоги и аналитика' },
                  { value: 'admin', label: 'Администратор', detail: 'Настройки проекта и доступы' },
                ]}
                onChange={(value) => setRole(value as 'admin' | 'manager')}
              />
            </div>
            {project.isOwner ? (
              <label className="access-switch-row">
                <span>
                  <b>Все мои проекты</b>
                  <small>Включая проекты, которые будут созданы позднее</small>
                </span>
                <input
                  type="checkbox"
                  checked={allProjects}
                  onChange={(event) => setAllProjects(event.target.checked)}
                />
              </label>
            ) : (
              <div className="access-switch-row access-switch-row--muted">
                <span>
                  <b>Только этот проект</b>
                  <small>Доступ ко всем проектам может выдать только их владелец</small>
                </span>
                <ShieldCheck size={18} />
              </div>
            )}
          </div>
        )}

        {lookup && role === 'manager' && !allProjects && (
          <fieldset className="access-channels">
            <legend>Каналы</legend>
            <label className="access-channel access-channel--all">
              <input type="checkbox" checked={allChannels} onChange={(event) => setAllChannels(event.target.checked)} />
              <span>
                <b>Все каналы проекта</b>
                <small>Новые каналы тоже будут доступны</small>
              </span>
            </label>
            {!allChannels &&
              overview.channels.map((channel) => (
                <label className="access-channel" key={channel.id}>
                  <input
                    type="checkbox"
                    checked={selectedChannels.includes(channel.id)}
                    onChange={(event) =>
                      setSelectedChannels((items) =>
                        event.target.checked ? [...items, channel.id] : items.filter((id) => id !== channel.id),
                      )
                    }
                  />
                  <span>
                    <b>{channel.customName || channel.name}</b>
                    <small>{channel.provider === 'vk' ? 'ВКонтакте' : 'Telegram'}</small>
                  </span>
                </label>
              ))}
          </fieldset>
        )}

        {lookup && (
          <div className="access-save-row">
            <p>
              {role === 'admin'
                ? 'Администратор сможет менять настройки и управлять доступами.'
                : 'Менеджер увидит только диалоги и аналитику выбранных каналов.'}
            </p>
            <button className="button button--dark" type="button" disabled={loading} onClick={saveAccess}>
              {lookup.registered ? 'Сохранить доступ' : 'Создать приглашение'}
            </button>
          </div>
        )}

        {inviteLink && (
          <div className="access-invite-link">
            <span>
              <b>Ссылка-приглашение готова</b>
              <small>Она действует 14 дней и привязана к указанному ID.</small>
            </span>
            <code>{inviteLink}</code>
            <button
              className="button button--outline"
              type="button"
              onClick={() =>
                void navigator.clipboard
                  .writeText(inviteLink)
                  .then(() => notify('Ссылка скопирована'))
                  .catch(() => notify('Не удалось скопировать ссылку', 'error'))
              }
            >
              <Copy size={15} /> Копировать
            </button>
          </div>
        )}
      </section>

      <section className="access-list-section">
        <div className="access-list-head">
          <div>
            <h3>Кому выдан доступ</h3>
            <p>Активные пользователи и ещё не принятые приглашения.</p>
          </div>
          <span>{entries.length}</span>
        </div>
        <div className="access-list">
          {entries.length === 0 && <div className="empty-inline">Кроме вас, доступа пока ни у кого нет.</div>}
          {entries.map((entry) => {
            const channelNames =
              entry.channelIds
                ?.map(
                  (id) =>
                    overview.channels.find((channel) => channel.id === id)?.customName ||
                    overview.channels.find((channel) => channel.id === id)?.name,
                )
                .filter(Boolean) ?? [];
            return (
              <article key={`${entry.kind}-${entry.id}`}>
                <span className="access-avatar">{entry.displayName.charAt(0)}</span>
                <div>
                  <b>{entry.displayName}</b>
                  <small>
                    {entry.identifierType === 'vk' ? 'VK ID' : 'FanLead ID'} {entry.identifier}
                  </small>
                </div>
                <span className={`access-status access-status--${entry.status}`}>
                  {entry.status === 'active' ? 'Активен' : 'Ожидает'}
                </span>
                <span className="role-chip">{entry.role === 'admin' ? 'Администратор' : 'Менеджер'}</span>
                <span className="access-entry-scope">
                  {entry.allProjects
                    ? 'Все проекты, включая новые'
                    : entry.channelIds
                      ? channelNames.join(', ') || `${entry.channelIds.length} каналов`
                      : 'Весь проект'}
                </span>
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Отозвать доступ"
                  onClick={() => {
                    if (!confirm('Отозвать этот доступ?')) return;
                    if (demoMode) {
                      setEntries((items) => items.filter((item) => item.id !== entry.id));
                      notify('Доступ отозван');
                      return;
                    }
                    void api(`/api/projects/${project.id}/access/${entry.id}`, { method: 'DELETE' }, csrf)
                      .then(() => {
                        setReloadVersion((value) => value + 1);
                        notify('Доступ отозван');
                      })
                      .catch((caught: Error) => notify(caught.message, 'error'));
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function LabelSettings({
  project,
  overview,
  disabled,
  onSave,
  csrf,
}: {
  project: Project;
  overview: Overview;
  disabled: boolean;
  onSave: SettingsAction;
  csrf: string | null;
}) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#84cc16');
  return (
    <div className="labels-settings">
      <div className="panel__head">
        <div>
          <h2>Метки диалогов</h2>
          <p>Метки принадлежат этому проекту и помогают менеджерам быстро группировать лиды.</p>
        </div>
      </div>
      <form
        className="label-create"
        onSubmit={(event) => {
          event.preventDefault();
          const labelName = name.trim();
          if (!labelName) return;
          onSave(() =>
            api(
              `/api/projects/${project.id}/labels`,
              { method: 'POST', body: JSON.stringify({ name: labelName, color }) },
              csrf,
            ).then(() => setName('')),
          );
        }}
      >
        <input type="color" value={color} onChange={(event) => setColor(event.target.value)} aria-label="Цвет метки" />
        <input
          value={name}
          maxLength={40}
          onChange={(event) => setName(event.target.value)}
          placeholder="Например, Горячий лид"
          disabled={disabled}
        />
        <button className="button button--dark" disabled={disabled || !name.trim()}>
          <Plus size={16} /> Создать метку
        </button>
      </form>
      <div className="labels-list">
        {overview.labels.map((label) => (
          <div key={label.id}>
            <span>
              <i style={{ background: label.color }} /> {label.name}
            </span>
            <button
              type="button"
              className="icon-button"
              aria-label={`Удалить метку ${label.name}`}
              disabled={disabled}
              onClick={() =>
                onSave(() => api(`/api/projects/${project.id}/labels/${label.id}`, { method: 'DELETE' }, csrf))
              }
            >
              <X size={16} />
            </button>
          </div>
        ))}
        {!overview.labels.length && <div className="empty-inline">Метки ещё не созданы.</div>}
      </div>
    </div>
  );
}

function CommentSettings({
  project,
  overview,
  disabled,
  onSave,
  csrf,
}: {
  project: Project;
  overview: Overview;
  disabled: boolean;
  onSave: SettingsAction;
  csrf: string | null;
}) {
  const defaults = overview.comments ?? {
    enabled: false,
    channelIds: [],
    keywords: [],
    prompt:
      'Ответь на комментарий от лица сообщества. Будь полезным, кратким и естественным. Не выдумывай факты, цены и условия.',
  };
  const vkChannels = overview.channels.filter((channel) => channel.provider === 'vk');
  const [enabled, setEnabled] = useState(defaults.enabled);
  const [channelIds, setChannelIds] = useState(defaults.channelIds);
  const [keywords, setKeywords] = useState(defaults.keywords.join(', '));
  const [prompt, setPrompt] = useState(defaults.prompt);
  useEffect(() => {
    setEnabled(defaults.enabled);
    setChannelIds(defaults.channelIds);
    setKeywords(defaults.keywords.join(', '));
    setPrompt(defaults.prompt);
  }, [defaults.enabled, defaults.channelIds, defaults.keywords, defaults.prompt]);
  return (
    <div className="automation-settings">
      <div className="panel__head">
        <div>
          <h2>AI-комментарии</h2>
          <p>OpenAI создаёт текст по вашему промпту, а SalesAI публикует ответ от имени сообщества VK.</p>
        </div>
        <label className="automation-toggle">
          <input type="checkbox" checked={enabled} disabled={disabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>{enabled ? 'Включено' : 'Выключено'}</span>
        </label>
      </div>
      <div className="automation-flow">
        <span>
          <b>1</b> Комментарий VK
        </span>
        <ArrowRight />
        <span>
          <b>2</b> Промпт + OpenAI
        </span>
        <ArrowRight />
        <span>
          <b>3</b> Ответ в VK
        </span>
      </div>
      <form
        className="form-stack automation-form"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(
            () =>
              api(
                `/api/projects/${project.id}/comment-settings`,
                {
                  method: 'PUT',
                  body: JSON.stringify({
                    enabled,
                    channelIds,
                    keywords: keywords
                      .split(',')
                      .map((item) => item.trim())
                      .filter(Boolean),
                    prompt,
                  }),
                },
                csrf,
              ),
            'Настройки AI-комментариев сохранены',
          );
        }}
      >
        <fieldset className="channel-picker" disabled={disabled}>
          <legend>Сообщества VK</legend>
          {vkChannels.map((channel) => (
            <label key={channel.id}>
              <input
                type="checkbox"
                checked={channelIds.includes(channel.id)}
                onChange={(event) =>
                  setChannelIds((current) =>
                    event.target.checked ? [...current, channel.id] : current.filter((id) => id !== channel.id),
                  )
                }
              />
              {channel.customName || channel.name}
            </label>
          ))}
          {!vkChannels.length && <small>Сначала подключите канал VK.</small>}
          <small>Если ничего не выбрано, настройка применяется ко всем каналам VK проекта.</small>
        </fieldset>
        <label>
          Ключевые слова
          <input
            value={keywords}
            onChange={(event) => setKeywords(event.target.value)}
            disabled={disabled}
            placeholder="прайс, цена, купить — или оставьте пустым для всех комментариев"
          />
          <small>Несколько слов разделяйте запятыми. Регистр не учитывается.</small>
        </label>
        <label>
          Промпт для ответов
          <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} rows={9} disabled={disabled} />
          <small>В промпт автоматически передаётся исходный комментарий. Модель берётся из настроек канала.</small>
        </label>
        <div className="form-footer">
          <small>В постах и обсуждениях ответ идёт в ветку; если VK не разрешает — с упоминанием автора.</small>
          <button className="button button--dark" disabled={disabled || prompt.trim().length < 20}>
            Сохранить AI-комментарии
          </button>
        </div>
      </form>
    </div>
  );
}

function IntegrationSettings(props: Parameters<typeof AmoCrmIntegrationCard>[0]) {
  return (
    <div className="integration-settings">
      <div className="panel__head">
        <div>
          <h2>Интеграции</h2>
          <p>Подключайте внешние системы отдельно от каналов общения.</p>
        </div>
      </div>
      <AmoCrmIntegrationCard {...props} />
    </div>
  );
}

function AmoCrmIntegrationCard({
  project,
  csrf,
  disabled,
  notify,
}: {
  project: Project;
  csrf: string | null;
  disabled: boolean;
  notify: Notify;
}) {
  const [integration, setIntegration] = useState<AmoCrmIntegrationStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (demoMode) {
      setIntegration({
        configured: false,
        missingSettings: ['AMO_CLIENT_ID', 'AMO_CHAT_CHANNEL_ID'],
        redirectUri: `${location.origin}/api/integrations/amocrm/callback`,
        webhookUrl: `${location.origin}/webhooks/amocrm/:scope_id`,
        status: 'not_connected',
      });
      return;
    }
    try {
      setIntegration(await api<AmoCrmIntegrationStatus>(`/api/projects/${project.id}/integrations/amocrm`));
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : 'Не удалось проверить amoCRM', 'error');
    }
  }, [notify, project.id]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.origin !== location.origin || event.data?.type !== 'salesai:amocrm') return;
      notify(
        event.data.ok ? 'amoCRM подключена' : event.data.message || 'Не удалось подключить amoCRM',
        event.data.ok ? 'success' : 'error',
      );
      void load();
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [load, notify]);
  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    notify('Скопировано');
  };
  const connect = async () => {
    setLoading(true);
    try {
      const result = await api<{ authUrl: string }>(
        `/api/projects/${project.id}/integrations/amocrm/connect`,
        { method: 'POST' },
        csrf,
      );
      const popup = window.open(result.authUrl, 'salesai-amocrm', 'popup,width=720,height=760');
      if (!popup) location.href = result.authUrl;
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : 'Не удалось начать подключение', 'error');
    } finally {
      setLoading(false);
    }
  };
  const disconnect = async () => {
    if (!confirm('Отключить amoCRM от этого проекта?')) return;
    setLoading(true);
    try {
      await api(`/api/projects/${project.id}/integrations/amocrm`, { method: 'DELETE' }, csrf);
      notify('amoCRM отключена');
      await load();
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : 'Не удалось отключить amoCRM', 'error');
    } finally {
      setLoading(false);
    }
  };
  if (!integration) return <div className="amocrm-card amocrm-card--loading">Проверяем amoCRM…</div>;
  const active = integration.status === 'active';
  return (
    <section className={`amocrm-card${active ? ' amocrm-card--active' : ''}`}>
      <div className="amocrm-card__head">
        <span className="amocrm-logo">amo</span>
        <div>
          <h3>amoCRM</h3>
          <p>Диалоги SalesAI появятся в amoCRM, а ответы менеджеров уйдут клиенту в исходный канал.</p>
        </div>
        <span className={`status status--${active ? 'active' : integration.status === 'error' ? 'error' : 'pending'}`}>
          {active
            ? 'Подключено'
            : integration.status === 'pending'
              ? 'Подключение'
              : integration.status === 'error'
                ? 'Ошибка'
                : 'Не подключено'}
        </span>
      </div>
      {active ? (
        <div className="amocrm-card__connected">
          <div>
            <b>{integration.accountName || integration.accountDomain}</b>
            <small>{integration.accountDomain}</small>
          </div>
          <span>Сообщения синхронизируются автоматически</span>
          <button
            className="button button--outline"
            type="button"
            disabled={disabled || loading}
            onClick={() => void disconnect()}
          >
            Отключить
          </button>
        </div>
      ) : (
        <>
          {!integration.configured && (
            <div className="amocrm-notice">
              <ShieldCheck size={18} />
              <span>
                Инфраструктура готова. Чтобы включить её, зарегистрируйте OAuth-интеграцию и канал Chats API, затем
                добавьте выданные реквизиты на сервер.
              </span>
            </div>
          )}
          {integration.statusDetail && <div className="notice notice--error">{integration.statusDetail}</div>}
          <div className="amocrm-endpoints">
            <label>
              Redirect URI
              <button type="button" onClick={() => void copy(integration.redirectUri)} title="Скопировать">
                <code>{integration.redirectUri}</code>
                <Copy size={15} />
              </button>
            </label>
            <label>
              Webhook Chats API
              <button type="button" onClick={() => void copy(integration.webhookUrl)} title="Скопировать">
                <code>{integration.webhookUrl}</code>
                <Copy size={15} />
              </button>
            </label>
          </div>
          <div className="amocrm-card__actions">
            <small>Настройка выполняется один раз; затем каждый проект подключается к своему аккаунту amoCRM.</small>
            <button
              className="button button--dark"
              type="button"
              disabled={disabled || loading || !integration.configured}
              onClick={() => void connect()}
            >
              <Zap size={17} /> {loading ? 'Подождите…' : 'Подключить amoCRM'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function ChannelsSettings({
  project,
  overview,
  disabled,
  onSave,
  csrf,
}: {
  project: Project;
  overview: Overview;
  disabled: boolean;
  onSave: SettingsAction;
  csrf: string | null;
}) {
  const [provider, setProvider] = useState<'telegram' | 'vk'>('telegram');
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [model, setModel] = useState(overview.ai?.defaultModel ?? 'gpt-5-mini');
  const [promptVersionId, setPromptVersionId] = useState('');
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (!model && overview.ai?.defaultModel) setModel(overview.ai.defaultModel);
  }, [overview.ai?.defaultModel, model]);
  return (
    <div>
      <div className="panel__head">
        <div>
          <h2>Подключённые каналы</h2>
          <p>SalesAI проверит токен, получит название, аватар и данные канала, затем создаст webhook.</p>
        </div>
        <button className="button button--dark" type="button" disabled={disabled} onClick={() => setAdding(true)}>
          <Plus size={16} /> Добавить канал
        </button>
      </div>
      <div className="channel-list">
        {overview.channels.map((channel) => (
          <ChannelSettingsRow
            key={channel.id}
            channel={channel}
            prompts={overview.prompts}
            project={project}
            disabled={disabled}
            onSave={onSave}
            csrf={csrf}
          />
        ))}
        {!overview.channels.length && <div className="empty-inline">Пока нет подключённых каналов.</div>}
      </div>
      {adding && (
        <form
          className="form-stack compact-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSave(() =>
              api(
                `/api/projects/${project.id}/channels`,
                {
                  method: 'POST',
                  body: JSON.stringify({
                    provider,
                    name: name.trim() || undefined,
                    token,
                    model,
                    promptVersionId: promptVersionId || undefined,
                  }),
                },
                csrf,
              ),
            );
          }}
        >
          <div className="form-title-row">
            <h3>Добавить канал</h3>
            <button className="icon-button" type="button" aria-label="Закрыть форму" onClick={() => setAdding(false)}>
              <X size={17} />
            </button>
          </div>
          <div className="segmented">
            <button
              type="button"
              className={provider === 'telegram' ? 'active' : ''}
              onClick={() => setProvider('telegram')}
            >
              Telegram Bot
            </button>
            <button type="button" className={provider === 'vk' ? 'active' : ''} onClick={() => setProvider('vk')}>
              VK Сообщество
            </button>
          </div>
          <label>
            Своё название <small className="field-help">Необязательно</small>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например, Основной канал — официальное название получим по токену"
              disabled={disabled}
            />
          </label>
          <label>
            Токен
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Будет зашифрован после отправки"
              disabled={disabled}
              required
            />
          </label>
          <div className="select-field">
            <span>AI-агент</span>
            <CustomSelect
              value={promptVersionId}
              options={[
                { value: '', label: 'Без AI-агента — только ручные ответы' },
                ...overview.prompts.map((prompt) => ({
                  value: prompt.versionId,
                  label: prompt.name,
                })),
              ]}
              onChange={setPromptVersionId}
              disabled={disabled}
              ariaLabel="Выберите AI-агента"
            />
          </div>
          <div className="select-field">
            <span>Модель OpenAI</span>
            <ModelSelect value={model} onChange={setModel} disabled={disabled} />
          </div>
          <button className="button button--dark" disabled={disabled}>
            <Plus size={17} /> Подключить
          </button>
        </form>
      )}
    </div>
  );
}

function ChannelSettingsRow({
  channel,
  prompts,
  project,
  disabled,
  onSave,
  csrf,
}: {
  channel: Overview['channels'][number];
  prompts: Overview['prompts'];
  project: Project;
  disabled: boolean;
  onSave: SettingsAction;
  csrf: string | null;
}) {
  const [promptVersionId, setPromptVersionId] = useState(channel.promptVersionId ?? '');
  const [model, setModel] = useState(channel.model);
  const [aiEnabled, setAiEnabled] = useState(channel.aiEnabled && Boolean(channel.promptVersionId));
  const [timeZone, setTimeZone] = useState(channel.timeZone);
  const [workDays, setWorkDays] = useState(channel.workDays);
  const [workdayStart, setWorkdayStart] = useState(channel.workdayStart);
  const [workdayEnd, setWorkdayEnd] = useState(channel.workdayEnd);
  useEffect(() => {
    setPromptVersionId(channel.promptVersionId ?? '');
    setModel(channel.model);
    setAiEnabled(channel.aiEnabled && Boolean(channel.promptVersionId));
    setTimeZone(channel.timeZone);
    setWorkDays(channel.workDays);
    setWorkdayStart(channel.workdayStart);
    setWorkdayEnd(channel.workdayEnd);
  }, [
    channel.promptVersionId,
    channel.model,
    channel.aiEnabled,
    channel.timeZone,
    channel.workDays,
    channel.workdayStart,
    channel.workdayEnd,
  ]);
  const username = typeof channel.providerMetadata?.username === 'string' ? channel.providerMetadata.username : null;
  const displayName = channel.customName || channel.name;
  const officialName = channel.customName ? channel.name : null;
  const screenName =
    typeof channel.providerMetadata?.screenName === 'string' ? channel.providerMetadata.screenName : null;
  const channelUrl = username ? `https://t.me/${username}` : screenName ? `https://vk.com/${screenName}` : undefined;
  return (
    <article className="channel-item">
      <a
        className={`channel-identity${channel.status === 'active' ? '' : ' channel-identity--with-status'}`}
        href={channelUrl}
        target="_blank"
        rel="noreferrer"
      >
        <span className="channel-avatar-wrap">
          {channel.avatarUrl ? (
            <img className="channel-avatar" src={channel.avatarUrl} alt="" />
          ) : (
            <span className={`provider-icon ${channel.provider}`}>
              {channel.provider === 'telegram' ? <Send /> : <Hash />}
            </span>
          )}
          <i className={`provider-badge ${channel.provider}`}>{channel.provider === 'telegram' ? 'TG' : 'VK'}</i>
        </span>
        <span>
          <b title={displayName}>{displayName}</b>
          <small title={officialName ?? undefined}>
            {officialName ??
              (username ? `@${username}` : channel.provider === 'telegram' ? 'Telegram Bot' : 'Сообщество')}
          </small>
          {channelUrl && <small className="channel-link">{channelUrl.replace(/^https:\/\//, '')}</small>}
        </span>
        {channel.status !== 'active' && (
          <em className={`status status--${channel.status}`}>
            {channel.status === 'pending' ? 'Подключаем' : channel.status}
          </em>
        )}
      </a>
      <div className="channel-controls">
        <div className={`channel-agent-control${aiEnabled ? '' : ' channel-agent-control--off'}`}>
          <span className="channel-agent-control__head">
            <span>AI-агент</span>
            <button
              type="button"
              className={`ai-toggle${aiEnabled ? ' ai-toggle--on' : ''}`}
              role="switch"
              aria-checked={aiEnabled}
              aria-label={aiEnabled ? 'Выключить AI-агента' : 'Включить AI-агента'}
              disabled={disabled || !promptVersionId}
              onClick={() => setAiEnabled((enabled) => !enabled)}
            >
              <i />
              <b>{aiEnabled ? 'ON' : 'OFF'}</b>
            </button>
          </span>
          <CustomSelect
            value={promptVersionId}
            options={[
              { value: '', label: 'Без AI-агента' },
              ...prompts.map((prompt) => ({
                value: prompt.versionId,
                label: prompt.name,
              })),
            ]}
            onChange={(nextPromptVersionId) => {
              setPromptVersionId(nextPromptVersionId);
              if (!nextPromptVersionId) setAiEnabled(false);
            }}
            disabled={disabled}
            ariaLabel="Выберите AI-агента"
          />
        </div>
        <div className="select-field">
          <span>Модель OpenAI</span>
          <ModelSelect value={model} onChange={setModel} disabled={disabled} />
        </div>
        <button
          type="button"
          className="button button--outline"
          disabled={disabled || !model.trim()}
          onClick={() =>
            onSave(() =>
              api(
                `/api/projects/${project.id}/channels/${channel.id}`,
                {
                  method: 'PUT',
                  body: JSON.stringify({
                    promptVersionId: promptVersionId || null,
                    model,
                    aiEnabled: aiEnabled && Boolean(promptVersionId),
                    schedule: { timeZone, workDays, workdayStart, workdayEnd },
                  }),
                },
                csrf,
              ),
            )
          }
        >
          Сохранить
        </button>
      </div>
      <details className="channel-schedule">
        <summary>
          <span>
            <Clock size={16} /> Рабочее время
          </span>
          <small>
            {workdayStart}–{workdayEnd} · {channelTimeZones.find(([value]) => value === timeZone)?.[1] ?? timeZone}
          </small>
        </summary>
        <div className="channel-schedule__body">
          <label>
            Часовой пояс
            <select value={timeZone} disabled={disabled} onChange={(event) => setTimeZone(event.target.value)}>
              {channelTimeZones.map(([value, label]) => (
                <option key={value} value={value}>
                  {label} · {value}
                </option>
              ))}
            </select>
          </label>
          <div className="channel-schedule__time">
            <label>
              Начало
              <input
                type="time"
                value={workdayStart}
                disabled={disabled}
                onChange={(event) => setWorkdayStart(event.target.value)}
              />
            </label>
            <label>
              Конец
              <input
                type="time"
                value={workdayEnd}
                disabled={disabled}
                onChange={(event) => setWorkdayEnd(event.target.value)}
              />
            </label>
          </div>
          <fieldset className="channel-workdays">
            <legend>Рабочие дни</legend>
            {weekDays.map((label, index) => {
              const day = index + 1;
              return (
                <label key={day}>
                  <input
                    type="checkbox"
                    checked={workDays.includes(day)}
                    disabled={disabled || (workDays.length === 1 && workDays.includes(day))}
                    onChange={(event) =>
                      setWorkDays((current) =>
                        event.target.checked ? [...current, day].sort() : current.filter((item) => item !== day),
                      )
                    }
                  />
                  {label}
                </label>
              );
            })}
          </fieldset>
          <p>Дожимы вне этого окна переносятся. Договорённость на нерабочее время AI не подтвердит.</p>
        </div>
      </details>
    </article>
  );
}

function FollowUpEditor({
  enabled,
  onEnabledChange,
  steps,
  onStepsChange,
  disabled,
}: {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  steps: FollowUpStepDraft[];
  onStepsChange: (steps: FollowUpStepDraft[]) => void;
  disabled: boolean;
}) {
  const updateStep = (index: number, change: Partial<FollowUpStepDraft>) =>
    onStepsChange(steps.map((step, stepIndex) => (stepIndex === index ? { ...step, ...change } : step)));
  const addStep = () => {
    if (steps.length >= maxFollowUpStepCount) return;
    const fallback = defaultFollowUpSteps[steps.length] ?? defaultFollowUpSteps.at(-1);
    if (!fallback) return;
    onStepsChange([...steps, followUpStepDraft({ ...fallback, stepOrder: steps.length + 1 })]);
  };
  const removeStep = (index: number) =>
    onStepsChange(
      steps
        .filter((_, stepIndex) => stepIndex !== index)
        .map((step, stepIndex) => ({ ...step, stepOrder: stepIndex + 1 })),
    );
  return (
    <section className="follow-up-workflow">
      <header>
        <span>
          <b>Цепочка дожимов</b>
          <small>
            {followUpCountLabel(steps.length)} · первый таймер от исходящего сообщения, остальные — от предыдущего
            дожима
          </small>
        </span>
        <label className="follow-up-switch">
          <input
            type="checkbox"
            checked={enabled}
            disabled={disabled}
            onChange={(event) => onEnabledChange(event.target.checked)}
          />
          <i aria-hidden="true" />
          <span>{enabled ? 'Дожимы включены' : 'Дожимы выключены'}</span>
        </label>
      </header>
      <div className="follow-up-steps">
        {steps.map((step, index) => (
          <article className="follow-up-step" key={step.stepOrder}>
            <div className="follow-up-step__number">{step.stepOrder}</div>
            <div className="follow-up-step__content">
              <div className="follow-up-step__head">
                <span>
                  <b>Дожим {step.stepOrder}</b>
                  <small>{index === 0 ? 'после исходящего сообщения' : `после дожима ${index}`}</small>
                </span>
              </div>
              <div className="follow-up-step__controls">
                <div className="follow-up-delay">
                  <label>
                    <span>Отправить через</span>
                    <span className="follow-up-delay__control">
                      <Clock size={16} aria-hidden="true" />
                      <input
                        type="number"
                        min={1}
                        max={43200}
                        value={step.delayValue}
                        disabled={disabled || !enabled}
                        aria-label={`Задержка дожима ${step.stepOrder}`}
                        onChange={(event) =>
                          updateStep(index, { delayValue: Math.max(1, Number(event.target.value) || 1) })
                        }
                      />
                      <select
                        value={step.delayUnit}
                        disabled={disabled || !enabled}
                        aria-label={`Единица времени дожима ${step.stepOrder}`}
                        onChange={(event) => updateStep(index, { delayUnit: event.target.value as FollowUpUnit })}
                      >
                        <option value="minutes">минут</option>
                        <option value="hours">часов</option>
                        <option value="days">дней</option>
                      </select>
                    </span>
                  </label>
                </div>
                <span className="follow-up-step__tools">
                  <label className="standard-follow-up">
                    <input
                      type="checkbox"
                      checked={step.useStandardPrompt}
                      disabled={disabled || !enabled}
                      onChange={(event) =>
                        updateStep(index, {
                          useStandardPrompt: event.target.checked,
                          ...(!event.target.checked && !step.prompt?.trim() ? { prompt: standardFollowUpPrompt } : {}),
                        })
                      }
                    />
                    <Check size={13} aria-hidden="true" />
                    Стандартный промпт
                  </label>
                  {steps.length > 1 && (
                    <button
                      type="button"
                      className="follow-up-remove"
                      disabled={disabled || !enabled}
                      onClick={() => removeStep(index)}
                      aria-label={`Удалить дожим ${step.stepOrder}`}
                      title="Удалить дожим"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </span>
              </div>
              <label className={`follow-up-step__prompt${step.useStandardPrompt ? ' is-standard' : ''}`}>
                {step.useStandardPrompt ? 'Стандартный промпт' : 'Свой промпт этого дожима'}
                <textarea
                  rows={4}
                  value={step.useStandardPrompt ? standardFollowUpPrompt : (step.prompt ?? '')}
                  readOnly={step.useStandardPrompt}
                  disabled={disabled || !enabled}
                  onChange={(event) => updateStep(index, { prompt: event.target.value })}
                  placeholder="Например: напомни о расчёте, подчеркни выгоду и предложи выбрать время звонка."
                  aria-invalid={!step.useStandardPrompt && (step.prompt?.trim().length ?? 0) < 3}
                />
                <small
                  className={!step.useStandardPrompt && (step.prompt?.trim().length ?? 0) < 3 ? 'field-error' : ''}
                >
                  {step.useStandardPrompt
                    ? 'Снимите «Стандартный промпт», чтобы взять этот текст за основу и изменить его.'
                    : (step.prompt?.trim().length ?? 0) < 3
                      ? 'Введите хотя бы 3 символа — после этого дожим можно сохранить.'
                      : 'Последний фрагмент переписки будет передан AI автоматически.'}
                </small>
              </label>
            </div>
          </article>
        ))}
      </div>
      <div className="follow-up-workflow__footer">
        <button
          type="button"
          className="button button--outline follow-up-add"
          disabled={disabled || !enabled || steps.length >= maxFollowUpStepCount}
          onClick={addStep}
        >
          <Plus size={17} />
          {steps.length >= maxFollowUpStepCount ? 'Добавлено максимум 5' : 'Добавить ещё дожим'}
        </button>
        <p className="follow-up-note">
          Цепочка остановится при успехе, жёстком негативе, точной договорённости или передаче менеджеру.
        </p>
      </div>
    </section>
  );
}

function PromptSettings({
  project,
  overview,
  disabled,
  onSave,
  csrf,
}: {
  project: Project;
  overview: Overview;
  disabled: boolean;
  onSave: SettingsAction;
  csrf: string | null;
}) {
  const [name, setName] = useState('Первичная квалификация');
  const [content, setContent] = useState(
    'Ты — внимательный менеджер по продажам. Выясни потребность клиента, ответь по существу и предложи следующий конкретный шаг. Не выдумывай цены и условия.',
  );
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    setCreating(overview.prompts.length === 0);
  }, [overview.prompts.length]);
  return (
    <div className="prompt-settings">
      <div className="panel__head">
        <div>
          <h2>AI-агенты проекта</h2>
          <p>
            У каждого канала работает один промпт. При новом назначении канал автоматически переключится на выбранного
            агента.
          </p>
        </div>
        {!creating && (
          <button type="button" className="button button--dark" disabled={disabled} onClick={() => setCreating(true)}>
            <Plus size={16} /> Новый AI-агент
          </button>
        )}
      </div>
      <section className="prompt-list-section">
        <div className="prompt-list-heading">
          <span>
            <b>Ваши AI-агенты</b>
            <small>Созданные и доступные для назначения каналам</small>
          </span>
          <i>{overview.prompts.length}</i>
        </div>
        <div className="prompt-list">
          {overview.prompts.map((prompt) => (
            <PromptAgentCard
              key={prompt.id}
              prompt={prompt}
              channels={overview.channels}
              prompts={overview.prompts}
              project={project}
              disabled={disabled}
              onSave={onSave}
              csrf={csrf}
            />
          ))}
          {!overview.prompts.length && (
            <div className="empty-inline">Пока нет AI-агентов. Заполните форму ниже, чтобы создать первого.</div>
          )}
        </div>
      </section>
      {creating && (
        <form
          className="form-stack prompt-create"
          onSubmit={(event) => {
            event.preventDefault();
            onSave(async () => {
              const created = await api<{ id: string }>(
                `/api/projects/${project.id}/prompts`,
                { method: 'POST', body: JSON.stringify({ name, content }) },
                csrf,
              );
              if (channelIds.length) {
                await api(
                  `/api/projects/${project.id}/prompts/${created.id}/channels`,
                  { method: 'PUT', body: JSON.stringify({ channelIds }) },
                  csrf,
                );
              }
              setCreating(false);
              setChannelIds([]);
            });
          }}
        >
          <div className="panel__head prompt-create__head">
            <div>
              <span className="prompt-create__eyebrow">Создание</span>
              <h2>Новый AI-агент</h2>
              <p>Инструкция (промпт) определяет поведение агента. Модель выбирается отдельно.</p>
            </div>
          </div>
          <div className="prompt-tab-panel">
            <label>
              Название
              <input value={name} onChange={(e) => setName(e.target.value)} disabled={disabled} />
            </label>
            <label>
              Инструкция (промпт)
              <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={12} disabled={disabled} />
            </label>
            <ChannelPicker
              channels={overview.channels}
              prompts={overview.prompts}
              selectedPromptId={null}
              channelIds={channelIds}
              onChange={setChannelIds}
              disabled={disabled}
            />
          </div>
          <div className="form-footer">
            <small>{content.length.toLocaleString('ru-RU')} символов · токены считаются по фактическому usage</small>
            <span className="prompt-create__actions">
              {!!overview.prompts.length && (
                <button type="button" className="button button--outline" onClick={() => setCreating(false)}>
                  Отменить
                </button>
              )}
              <button
                className="button button--dark"
                disabled={disabled || name.trim().length < 2 || content.trim().length < 20}
              >
                <Plus size={16} /> Создать AI-агента
              </button>
            </span>
          </div>
        </form>
      )}
    </div>
  );
}

function ChannelPicker({
  channels,
  prompts,
  selectedPromptId,
  channelIds,
  onChange,
  disabled,
}: {
  channels: Overview['channels'];
  prompts: Overview['prompts'];
  selectedPromptId: string | null;
  channelIds: string[];
  onChange: (channelIds: string[]) => void;
  disabled: boolean;
}) {
  return (
    <fieldset className="channel-picker" disabled={disabled}>
      <legend>Источники агента</legend>
      <p>Включите каналы, где должен работать этот промпт.</p>
      <div className="channel-picker__grid">
        {channels.map((channel) => {
          const occupiedBy =
            channel.promptId && channel.promptId !== selectedPromptId
              ? prompts.find((candidate) => candidate.id === channel.promptId)
              : null;
          return (
            <label key={channel.id}>
              <input
                type="checkbox"
                checked={channelIds.includes(channel.id)}
                onChange={(event) =>
                  onChange(
                    event.target.checked ? [...channelIds, channel.id] : channelIds.filter((id) => id !== channel.id),
                  )
                }
              />
              <span>
                <b>{channel.customName || channel.name}</b>
                <small>
                  {occupiedBy
                    ? `Сейчас: ${occupiedBy.name}. После сохранения будет переключён.`
                    : channel.provider === 'vk'
                      ? 'VK'
                      : 'Telegram'}
                </small>
              </span>
            </label>
          );
        })}
      </div>
      {!channels.length && <small>Сначала подключите хотя бы один канал.</small>}
    </fieldset>
  );
}

function PromptAgentCard({
  prompt,
  channels,
  prompts,
  project,
  disabled,
  onSave,
  csrf,
}: {
  prompt: Overview['prompts'][number];
  channels: Overview['channels'];
  prompts: Overview['prompts'];
  project: Project;
  disabled: boolean;
  onSave: SettingsAction;
  csrf: string | null;
}) {
  const [channelIds, setChannelIds] = useState(prompt.channelIds);
  const [name, setName] = useState(prompt.name);
  const [content, setContent] = useState(prompt.content);
  const [editing, setEditing] = useState(false);
  const [editorTab, setEditorTab] = useState<'agent' | 'followups' | 'analysis'>('agent');
  const [successCriteria, setSuccessCriteria] = useState(prompt.successCriteria);
  const [negativeCriteria, setNegativeCriteria] = useState(prompt.negativeCriteria);
  const [agreementCriteria, setAgreementCriteria] = useState(prompt.agreementCriteria);
  const [handoffCriteria, setHandoffCriteria] = useState(prompt.handoffCriteria);
  const [negativeReplyLimit, setNegativeReplyLimit] = useState(prompt.negativeReplyLimit);
  const [notifyOnSuccess, setNotifyOnSuccess] = useState(prompt.notifyOnSuccess);
  const [notifyOnHandoff, setNotifyOnHandoff] = useState(prompt.notifyOnHandoff);
  const [followUpEnabled, setFollowUpEnabled] = useState(prompt.followUpEnabled);
  const [followUpSteps, setFollowUpSteps] = useState<FollowUpStepDraft[]>(normalizeFollowUpSteps(prompt.followUpSteps));
  useEffect(() => setChannelIds(prompt.channelIds), [prompt.channelIds]);
  useEffect(() => {
    setName(prompt.name);
    setContent(prompt.content);
    setSuccessCriteria(prompt.successCriteria);
    setNegativeCriteria(prompt.negativeCriteria);
    setAgreementCriteria(prompt.agreementCriteria);
    setHandoffCriteria(prompt.handoffCriteria);
    setNegativeReplyLimit(prompt.negativeReplyLimit);
    setNotifyOnSuccess(prompt.notifyOnSuccess);
    setNotifyOnHandoff(prompt.notifyOnHandoff);
    setFollowUpEnabled(prompt.followUpEnabled);
    setFollowUpSteps(normalizeFollowUpSteps(prompt.followUpSteps));
  }, [prompt]);
  const assignedChannels = channels.filter((channel) => prompt.channelIds.includes(channel.id));
  return (
    <article className={`prompt-card${editing ? ' prompt-card--editing' : ''}`}>
      <div className="prompt-card__summary">
        <span className="prompt-card__identity">
          <i>
            <Bot size={18} />
          </i>
          <span>
            <span className="prompt-card__title">
              <h3>{prompt.name}</h3>
            </span>
            <small>
              {assignedChannels.length
                ? `Работает в ${formatChannelCount(assignedChannels.length)}`
                : 'Не назначен ни одному каналу'}
            </small>
          </span>
        </span>
        {!editing && (
          <button type="button" className="button button--outline" disabled={disabled} onClick={() => setEditing(true)}>
            <Pencil size={15} /> Редактировать
          </button>
        )}
      </div>
      {!editing && (
        <div className="prompt-card__badges">
          {assignedChannels.map((channel) => (
            <span key={channel.id}>{channel.customName || channel.name}</span>
          ))}
          {!assignedChannels.length && <span className="muted-badge">Без источников</span>}
        </div>
      )}
      {editing && (
        <div className="agent-editor">
          <div className="agent-editor__tabs" role="tablist" aria-label="Настройки AI-агента">
            <button
              type="button"
              className={editorTab === 'agent' ? 'active' : ''}
              onClick={() => setEditorTab('agent')}
            >
              <Bot size={15} /> Агент
            </button>
            <button
              type="button"
              className={editorTab === 'followups' ? 'active' : ''}
              onClick={() => setEditorTab('followups')}
            >
              <Zap size={15} /> Дожимы
            </button>
            <button
              type="button"
              className={editorTab === 'analysis' ? 'active' : ''}
              onClick={() => setEditorTab('analysis')}
            >
              <Activity size={15} /> Цели и статусы
            </button>
          </div>
          {editorTab === 'agent' && (
            <div className="agent-editor__fields">
              <label>
                Название AI-агента
                <input
                  value={name}
                  disabled={disabled}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Например, Первичная квалификация"
                />
                <small>Это название видно только вашей команде. После изменения нажмите «Сохранить изменения».</small>
              </label>
              <label>
                Инструкция (промпт)
                <textarea
                  rows={12}
                  value={content}
                  disabled={disabled}
                  onChange={(event) => setContent(event.target.value)}
                />
                <small>{content.length.toLocaleString('ru-RU')} символов</small>
              </label>
              <ChannelPicker
                channels={channels}
                prompts={prompts}
                selectedPromptId={prompt.id}
                channelIds={channelIds}
                onChange={setChannelIds}
                disabled={disabled}
              />
            </div>
          )}
          {editorTab === 'followups' && (
            <FollowUpEditor
              enabled={followUpEnabled}
              onEnabledChange={setFollowUpEnabled}
              steps={followUpSteps}
              onStepsChange={setFollowUpSteps}
              disabled={disabled}
            />
          )}
          {editorTab === 'analysis' && (
            <div className="outcome-settings">
              <div className="outcome-settings__intro">
                <b>AI анализирует каждый ответ клиента</b>
                <p>
                  Успех, жёсткий негатив и передача менеджеру останавливают AI и дожимы. После этого диалог можно вести
                  вручную или снова запустить AI верхней кнопкой. Точные договорённости определяются автоматически.
                </p>
              </div>
              <section className="outcome-rule outcome-rule--success">
                <header>
                  <span>
                    <Check size={16} />
                    <span>
                      <b>Успех</b>
                      <small>После какого целевого действия перестать отвечать</small>
                    </span>
                  </span>
                  <label className="follow-up-switch outcome-notify">
                    <input
                      type="checkbox"
                      checked={notifyOnSuccess}
                      disabled={disabled}
                      onChange={(event) => setNotifyOnSuccess(event.target.checked)}
                    />
                    <i aria-hidden="true" />
                    <span>Уведомить менеджеров</span>
                  </label>
                </header>
                <textarea
                  rows={4}
                  value={successCriteria}
                  disabled={disabled}
                  onChange={(event) => setSuccessCriteria(event.target.value)}
                />
                <small>Уведомление увидят в SalesAI менеджеры, у которых есть доступ к каналу.</small>
              </section>
              <section className="outcome-rule outcome-rule--handoff">
                <header>
                  <span>
                    <Users size={16} />
                    <span>
                      <b>Передать менеджеру</b>
                      <small>В каких случаях AI должен остановиться и позвать человека</small>
                    </span>
                  </span>
                  <label className="follow-up-switch outcome-notify">
                    <input
                      type="checkbox"
                      checked={notifyOnHandoff}
                      disabled={disabled}
                      onChange={(event) => setNotifyOnHandoff(event.target.checked)}
                    />
                    <i aria-hidden="true" />
                    <span>Уведомить менеджеров</span>
                  </label>
                </header>
                <textarea
                  rows={4}
                  value={handoffCriteria}
                  disabled={disabled}
                  onChange={(event) => setHandoffCriteria(event.target.value)}
                />
                <small>Диалог сразу перейдёт в ручной режим.</small>
              </section>
              <section className="outcome-rule outcome-rule--negative">
                <header>
                  <span>
                    <X size={16} />
                    <span>
                      <b>Негатив и возражения</b>
                      <small>Одна инструкция: как распознать негатив и что отвечать</small>
                    </span>
                  </span>
                </header>
                <textarea
                  rows={4}
                  value={negativeCriteria}
                  disabled={disabled}
                  onChange={(event) => setNegativeCriteria(event.target.value)}
                />
                <label className="negative-limit">
                  <span>Сколько раз подряд пытаться отработать возражение</span>
                  <select
                    value={negativeReplyLimit}
                    disabled={disabled}
                    onChange={(event) => setNegativeReplyLimit(Number(event.target.value))}
                  >
                    <option value={0}>Не отрабатывать</option>
                    <option value={1}>1 попытка</option>
                    <option value={2}>2 попытки</option>
                    <option value={3}>3 попытки</option>
                  </select>
                </label>
                <small>Если негатив продолжается дольше лимита, AI завершит диалог и перестанет отвечать.</small>
              </section>
            </div>
          )}
          <div className="prompt-card__actions">
            <button
              type="button"
              className="button button--outline"
              disabled={disabled}
              onClick={() => {
                setName(prompt.name);
                setContent(prompt.content);
                setChannelIds(prompt.channelIds);
                setSuccessCriteria(prompt.successCriteria);
                setNegativeCriteria(prompt.negativeCriteria);
                setAgreementCriteria(prompt.agreementCriteria);
                setHandoffCriteria(prompt.handoffCriteria);
                setNegativeReplyLimit(prompt.negativeReplyLimit);
                setNotifyOnSuccess(prompt.notifyOnSuccess);
                setNotifyOnHandoff(prompt.notifyOnHandoff);
                setFollowUpEnabled(prompt.followUpEnabled);
                setFollowUpSteps(normalizeFollowUpSteps(prompt.followUpSteps));
                setEditing(false);
              }}
            >
              Отменить
            </button>
            <button
              type="button"
              className="button button--dark"
              disabled={
                disabled ||
                (editorTab !== 'followups' &&
                  (name.trim().length < 2 ||
                    content.trim().length < 20 ||
                    successCriteria.trim().length < 20 ||
                    negativeCriteria.trim().length < 20 ||
                    agreementCriteria.trim().length < 20 ||
                    handoffCriteria.trim().length < 20)) ||
                (editorTab === 'followups' &&
                  followUpSteps.some(
                    (step) => !step.useStandardPrompt && (!step.prompt || step.prompt.trim().length < 3),
                  ))
              }
              onClick={() =>
                onSave(
                  async () => {
                    if (editorTab === 'followups') {
                      await api(
                        `/api/projects/${project.id}/prompts/${prompt.id}/follow-ups`,
                        {
                          method: 'PUT',
                          body: JSON.stringify({
                            enabled: followUpEnabled,
                            steps: followUpSteps.map(followUpStepPayload),
                          }),
                        },
                        csrf,
                      );
                    } else {
                      await api(
                        `/api/projects/${project.id}/prompts/${prompt.id}`,
                        {
                          method: 'PUT',
                          body: JSON.stringify({
                            name,
                            content,
                            successCriteria,
                            negativeCriteria,
                            agreementCriteria,
                            handoffCriteria,
                            negativeReplyLimit,
                            notifyOnSuccess,
                            notifyOnHandoff,
                          }),
                        },
                        csrf,
                      );
                      if (editorTab === 'agent') {
                        await api(
                          `/api/projects/${project.id}/prompts/${prompt.id}/channels`,
                          { method: 'PUT', body: JSON.stringify({ channelIds }) },
                          csrf,
                        );
                      }
                    }
                    setEditing(false);
                  },
                  editorTab === 'followups' ? 'Дожимы сохранены' : 'AI-агент сохранён',
                )
              }
            >
              {editorTab === 'followups' ? 'Сохранить дожимы' : 'Сохранить изменения'}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

function AiSettings({
  project,
  overview,
  disabled,
  onSave,
  csrf,
}: {
  project: Project;
  overview: Overview;
  disabled: boolean;
  onSave: SettingsAction;
  csrf: string | null;
}) {
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verification, setVerification] = useState({
    status: overview.ai?.keyStatus ?? 'unverified',
    keyHint: overview.ai?.keyHint,
    checkedAt: overview.ai?.keyCheckedAt,
    detail: overview.ai?.keyStatusDetail,
  });
  const [model, setModel] = useState(overview.ai?.defaultModel ?? 'gpt-5-mini');
  const rates = OPENAI_MODEL_RATES[model];
  useEffect(() => {
    setVerification({
      status: overview.ai?.keyStatus ?? 'unverified',
      keyHint: overview.ai?.keyHint,
      checkedAt: overview.ai?.keyCheckedAt,
      detail: overview.ai?.keyStatusDetail,
    });
  }, [overview.ai]);
  return (
    <form
      className="form-stack ai-settings"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(async () => {
          await api(
            `/api/projects/${project.id}/ai-settings`,
            {
              method: 'PUT',
              body: JSON.stringify({
                apiKey: apiKey || undefined,
                defaultModel: model,
                currency: 'USD',
              }),
            },
            csrf,
          );
          setApiKey('');
          setShowApiKey(false);
        });
      }}
    >
      <div className="ai-settings__intro">
        <span className="ai-settings__intro-icon">
          <KeyRound size={19} />
        </span>
        <div>
          <h2>OpenAI</h2>
          <p>Подключите ключ и выберите модель, которую будут использовать AI-агенты проекта.</p>
        </div>
      </div>

      <section className="openai-section">
        <div className="openai-section__head">
          <span className="openai-section__icon">
            <ShieldCheck size={17} />
          </span>
          <div>
            <h3>Подключение OpenAI</h3>
            <p>Статус ключа, проверка доступа и безопасная замена.</p>
          </div>
          <span
            className={`status status--${verification.status === 'active' ? 'active' : verification.status === 'error' ? 'error' : 'pending'}`}
          >
            {verification.status === 'active' ? 'Активен' : verification.status === 'error' ? 'Ошибка' : 'Не проверен'}
          </span>
        </div>

        <div className="openai-key-card">
          <div className="openai-key-card__head">
            <div>
              <small>Сохранённый ключ</small>
              <b>
                {overview.ai?.keyConfigured
                  ? (verification.keyHint ?? 'Сохранён — нажмите «Проверить»')
                  : 'Не добавлен'}
              </b>
              {verification.detail && <p>{verification.detail}</p>}
              {verification.checkedAt && <p>Проверен {new Date(verification.checkedAt).toLocaleString('ru-RU')}</p>}
            </div>
            <button
              className="button button--outline"
              type="button"
              disabled={disabled || !overview.ai?.keyConfigured || verifying}
              onClick={() => {
                setVerifying(true);
                onSave(async () => {
                  try {
                    const result = await api<{
                      status: 'active' | 'error';
                      keyHint: string;
                      checkedAt: string;
                      detail: string;
                    }>(`/api/projects/${project.id}/ai-settings/verify`, { method: 'POST' }, csrf);
                    setVerification(result);
                    if (result.status === 'error') throw new Error(result.detail || 'Ключ не прошёл проверку');
                  } finally {
                    setVerifying(false);
                  }
                }, 'Ключ проверен');
              }}
            >
              <ShieldCheck size={15} /> {verifying ? 'Проверяем…' : 'Проверить ключ'}
            </button>
          </div>

          {verification.status !== 'active' && (
            <div className="openai-key-warning">
              <ShieldCheck size={16} />
              <span>
                <b>AI-агенты временно отключены</b>
                <small>Диалоги продолжают поступать — отвечать можно вручную до успешной проверки ключа.</small>
              </span>
            </div>
          )}

          <div className="openai-key-tools">
            <label>
              {overview.ai?.keyConfigured ? 'Заменить ключ' : 'OpenAI API key'}
              <span className="secret-input">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={overview.ai?.keyConfigured ? 'Введите новый ключ только для замены' : 'sk-…'}
                  autoComplete="new-password"
                  disabled={disabled}
                />
                <button
                  type="button"
                  aria-label={showApiKey ? 'Скрыть ключ' : 'Показать ключ'}
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </span>
              <small className="field-help">Сохранённый ключ показывается только в виде безопасной маски.</small>
            </label>

            <details className="openai-permissions">
              <summary>
                <span>
                  <b>Какие права нужны ключу?</b>
                  <small>Три минимальных разрешения</small>
                </span>
                <ChevronDown size={16} />
              </summary>
              <ul>
                <li>
                  <span>List models</span>
                  <strong>Read</strong>
                </li>
                <li>
                  <span>Model capabilities</span>
                  <strong>Request</strong>
                </li>
                <li>
                  <span>Responses и Conversations</span>
                  <strong>Write</strong>
                </li>
              </ul>
              <p>
                Эти права должны быть у владельца ключа в выбранном OpenAI Project. После изменения ролей применение
                может занять до 30 минут.
              </p>
              <a href="https://developers.openai.com/api/docs/guides/rbac" target="_blank" rel="noreferrer">
                Официальная документация по правам
              </a>
            </details>
          </div>
        </div>
      </section>

      <section className="openai-section">
        <div className="openai-section__head">
          <span className="openai-section__icon">
            <Bot size={17} />
          </span>
          <div>
            <h3>Модель для AI-агентов</h3>
            <p>Используется по умолчанию, если для канала не выбрана другая модель.</p>
          </div>
        </div>
        <div className="openai-model-row">
          <div className="select-field">
            <span>Модель OpenAI по умолчанию</span>
            <ModelSelect value={model} onChange={setModel} disabled={disabled} />
          </div>
          <div className="openai-model-rate">
            <small>Тариф за 1 млн токенов</small>
            {rates ? (
              <>
                <b>
                  {rates.input} вход · {rates.output} ответ
                </b>
                <span>Кэшированный вход: {rates.cached}</span>
              </>
            ) : (
              <>
                <b>Тариф не указан</b>
                <span>Расходы этой модели пока не оцениваются автоматически.</span>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="openai-section">
        <div className="openai-section__head">
          <span className="openai-section__icon">
            <CircleDollarSign size={17} />
          </span>
          <div>
            <h3>Расходы и баланс</h3>
            <p>Оценка расходов проекта и ссылка на фактический баланс OpenAI.</p>
          </div>
        </div>
        <div className="openai-cost-grid">
          <section>
            <small>Расходы через SalesAI</small>
            <b>${Number(overview.summary.estimated_cost ?? 0).toFixed(4)}</b>
            <p>По запросам, которые уже прошли через этот проект.</p>
          </section>
          <section>
            <small>Выбранная модель</small>
            <b>{OPENAI_MODELS.find((option) => option.id === model)?.label ?? model}</b>
            <p>Расчёт обновляется автоматически по фактическому usage.</p>
          </section>
          <section>
            <small>Баланс OpenAI</small>
            <b>В кабинете OpenAI</b>
            <p>Project API key не предоставляет SalesAI доступ к балансу организации.</p>
            <a
              href="https://platform.openai.com/settings/organization/billing/overview"
              target="_blank"
              rel="noreferrer"
            >
              Открыть Billing OpenAI <ArrowRight size={13} />
            </a>
          </section>
        </div>
      </section>

      <div className="openai-actions">
        <p>Изменения модели и новый ключ применятся после сохранения.</p>
        <button className="button button--dark" disabled={disabled}>
          Сохранить настройки
        </button>
      </div>
    </form>
  );
}

function ProjectsPage({
  projects,
  session,
  onCreated,
  navigate,
  notify,
}: {
  projects: Project[];
  session: Session;
  onCreated: () => void;
  navigate: (path: string) => void;
  notify: Notify;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  return (
    <div className="page-stack">
      <div className="project-grid">
        <form
          className="project-card project-card--new"
          onSubmit={(event) => {
            event.preventDefault();
            void api('/api/projects', { method: 'POST', body: JSON.stringify({ name, slug }) }, session.csrfToken)
              .then(() => {
                setName('');
                setSlug('');
                onCreated();
                notify('Проект создан');
              })
              .catch((e: Error) => notify(e.message, 'error'));
          }}
        >
          <div className="project-create__heading">
            <span className="project-card__mark">
              <Plus />
            </span>
            <div>
              <h3>Создать проект</h3>
              <p>Добавьте название и короткий адрес</p>
            </div>
            <button className="button button--dark">Создать</button>
          </div>
          <div className="project-create__fields">
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setSlug(
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, ''),
                );
              }}
              aria-label="Название проекта"
              placeholder="Название проекта"
              required
            />
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              aria-label="Адрес проекта"
              placeholder="Короткий адрес"
              required
            />
          </div>
        </form>
        {projects.map((project) => (
          <a
            className="project-card"
            key={project.id}
            href={projectPath(project.slug, 'dialogs')}
            onClick={(event) => {
              event.preventDefault();
              navigate(projectPath(project.slug, 'dialogs'));
            }}
          >
            <div className="project-card__heading">
              <span className="project-card__mark">
                <Sparkles />
              </span>
              <div>
                <h3>{project.name}</h3>
                {project.sourceProjectName && <p>{project.sourceProjectName}</p>}
              </div>
            </div>
            <span className="role-chip">{project.role === 'admin' ? 'Администратор' : 'Менеджер'}</span>
            <dl className="project-card__stats">
              <div>
                <dt>Каналы</dt>
                <dd>{project.channelCount}</dd>
              </div>
              <div>
                <dt>Диалоги</dt>
                <dd>{project.conversationCount}</dd>
              </div>
              <div>
                <dt>Квалы</dt>
                <dd>{project.qualifiedCount}</dd>
              </div>
              <div>
                <dt>Стоимость</dt>
                <dd>${Number(project.estimatedCost).toFixed(2)}</dd>
              </div>
            </dl>
          </a>
        ))}
      </div>
    </div>
  );
}

function Cabinet({ session, setSession }: { session: Session; setSession: (session: Session) => void }) {
  const [route, setRoute] = useState<CabinetRoute>(() => parseCabinetRoute(location.pathname));
  const [toasts, setToasts] = useState<AppToast[]>([]);
  const toastSequence = useRef(0);
  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);
  const notify = useCallback<Notify>((message, kind = 'success') => {
    toastSequence.current += 1;
    setToasts((current) => [...current, { id: toastSequence.current, message, kind }]);
  }, []);
  const [channelFilter, setChannelFilterState] = useState(() =>
    parseCabinetRoute(location.pathname).tab === 'dialogs'
      ? (new URLSearchParams(location.search).get('channel') ?? 'all')
      : 'all',
  );
  const [overview, setOverview] = useState<Overview>(
    demoMode ? demoOverview : { summary: {}, channels: [], prompts: [], labels: [], comments: null, ai: null },
  );
  const [conversations, setConversations] = useState<Conversation[]>(demoMode ? demoConversations : []);
  const [messages, setMessages] = useState<ChatMessage[]>(demoMode ? demoMessages : []);
  const [messagesConversationId, setMessagesConversationId] = useState<string | undefined>(
    demoMode ? demoConversations[0]?.id : undefined,
  );
  const [messagesError, setMessagesError] = useState('');
  const [messagesReloadVersion, setMessagesReloadVersion] = useState(0);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>(demoMode ? demoConversations[0]?.id : undefined);
  const selectedConversationRef = useRef<string | undefined>(demoMode ? demoConversations[0]?.id : undefined);
  const messagesRequestVersion = useRef(0);
  const [typing, setTyping] = useState(false);
  const [modeMutationId, setModeMutationId] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => {
    const saved = localStorage.getItem('salesai-theme');
    return saved === 'light' || saved === 'dark' ? saved : 'system';
  });
  const project =
    session.projects.find((item) => item.slug === route.projectSlug) ??
    (demoMode && route.projectSlug === demoProject.slug ? demoProject : undefined);
  const [sidebarProjectId, setSidebarProjectId] = useState<string | undefined>(
    project?.id ?? session.projects[0]?.id ?? (demoMode ? demoProject.id : undefined),
  );
  const sidebarProject =
    project ??
    session.projects.find((item) => item.id === sidebarProjectId) ??
    session.projects[0] ??
    (demoMode ? demoProject : undefined);
  useEffect(() => {
    if (project) setSidebarProjectId(project.id);
  }, [project?.id]);
  useEffect(() => {
    if (!project || project.role === 'admin' || route.tab !== 'settings') return;
    const path = projectPath(project.slug, 'analytics');
    history.replaceState({}, '', path);
    setRoute(parseCabinetRoute(path));
  }, [project, route.tab]);
  const selectConversation = (conversationId: string | undefined) => {
    if (selectedConversationRef.current === conversationId) return;
    selectedConversationRef.current = conversationId;
    messagesRequestVersion.current += 1;
    setSelectedId(conversationId);
    setMessages([]);
    setMessagesConversationId(undefined);
    setMessagesError('');
    setHasOlderMessages(false);
    setLoadingOlderMessages(false);
    setTyping(false);
  };
  const navigate = (path: string, replace = false) => {
    const target = new URL(path, location.origin);
    const targetRoute = parseCabinetRoute(target.pathname);
    if (!target.search && targetRoute.tab === 'dialogs' && targetRoute.projectSlug === route.projectSlug) {
      const currentChannel = new URLSearchParams(location.search).get('channel');
      if (currentChannel) target.searchParams.set('channel', currentChannel);
    }
    if (replace) history.replaceState(null, '', `${target.pathname}${target.search}`);
    else history.pushState(null, '', `${target.pathname}${target.search}`);
    setRoute(targetRoute);
    setChannelFilterState(targetRoute.tab === 'dialogs' ? (target.searchParams.get('channel') ?? 'all') : 'all');
  };
  const setChannelFilter = (channelId: string) => {
    if (route.tab !== 'dialogs') return;
    const target = new URL(location.href);
    if (channelId === 'all') target.searchParams.delete('channel');
    else target.searchParams.set('channel', channelId);
    history.pushState(null, '', `${target.pathname}${target.search}`);
    setChannelFilterState(channelId);
  };
  const selectProject = (projectId: string) => {
    const nextProject = session.projects.find((item) => item.id === projectId);
    if (!nextProject) return;
    setSidebarProjectId(nextProject.id);
    const target = projectPath(
      nextProject.slug,
      route.tab === 'projects' ? 'dialogs' : route.tab,
      route.settingsSection,
    );
    history.pushState(null, '', target);
    setChannelFilterState('all');
    setRoute(parseCabinetRoute(target));
  };
  useEffect(() => {
    const media = matchMedia('(prefers-color-scheme: dark)');
    const applyTheme = () => {
      const resolved = themePreference === 'system' ? (media.matches ? 'dark' : 'light') : themePreference;
      document.documentElement.dataset.theme = resolved;
      document.documentElement.dataset.themePreference = themePreference;
    };
    applyTheme();
    media.addEventListener('change', applyTheme);
    localStorage.setItem('salesai-theme', themePreference);
    return () => media.removeEventListener('change', applyTheme);
  }, [themePreference]);
  useEffect(() => {
    const onPopState = () => {
      const nextRoute = parseCabinetRoute(location.pathname);
      setRoute(nextRoute);
      setChannelFilterState(
        nextRoute.tab === 'dialogs' ? (new URLSearchParams(location.search).get('channel') ?? 'all') : 'all',
      );
    };
    addEventListener('popstate', onPopState);
    return () => removeEventListener('popstate', onPopState);
  }, []);
  useEffect(() => {
    if (route.tab === 'dialogs') return;
    const target = new URL(location.href);
    if (!target.searchParams.has('channel')) return;
    target.searchParams.delete('channel');
    history.replaceState(null, '', `${target.pathname}${target.search}`);
    setChannelFilterState('all');
  }, [route.tab]);
  useEffect(() => {
    if (route.tab === 'projects') return;
    if (project) return;
    const first = session.projects[0] ?? (demoMode ? demoProject : undefined);
    navigate(first ? projectPath(first.slug, 'dialogs') : '/app/projects', true);
  }, [project, route.tab, session.projects]);
  useEffect(() => {
    if (!route.conversationId) return;
    const routed = conversations.find(
      (conversation) => conversation.publicId === route.conversationId || conversation.id === route.conversationId,
    );
    if (!routed) {
      if (!project || demoMode || !/^[0-9]{10}$/.test(route.conversationId)) return;
      let cancelled = false;
      void api<{ conversation: Conversation }>(
        `/api/projects/${project.id}/conversations/by-public-id/${route.conversationId}`,
      )
        .then(({ conversation }) => {
          if (cancelled) return;
          setConversations((items) =>
            items.some((item) => item.id === conversation.id) ? items : [conversation, ...items],
          );
          selectConversation(conversation.id);
        })
        .catch(() => {
          if (!cancelled) navigate(projectPath(project.slug, 'dialogs'), true);
        });
      return () => {
        cancelled = true;
      };
    }
    selectConversation(routed.id);
    if (project && route.conversationId !== routed.publicId) {
      navigate(`${projectPath(project.slug, 'dialogs')}/${routed.publicId}`, true);
    }
  }, [route.conversationId, conversations, project?.slug]);
  useEffect(() => {
    document.body.classList.toggle('dialogs-open', route.tab === 'dialogs');
    return () => document.body.classList.remove('dialogs-open');
  }, [route.tab]);
  useEffect(() => {
    if (route.tab === 'dialogs' && project && selectedId && !route.conversationId) {
      const selected = conversations.find((conversation) => conversation.id === selectedId);
      navigate(`${projectPath(project.slug, 'dialogs')}/${selected?.publicId ?? selectedId}`, true);
    }
  }, [project?.slug, route.tab, route.conversationId, selectedId]);
  const filteredChannel = overview.channels.find(
    (channel) => channel.publicId === channelFilter || channel.id === channelFilter,
  );
  useEffect(() => {
    if (
      route.tab !== 'dialogs' ||
      channelFilter === 'all' ||
      !filteredChannel ||
      channelFilter === filteredChannel.publicId
    )
      return;
    const target = new URL(location.href);
    target.searchParams.set('channel', filteredChannel.publicId);
    history.replaceState(null, '', `${target.pathname}${target.search}`);
    setChannelFilterState(filteredChannel.publicId);
  }, [route.tab, channelFilter, filteredChannel?.publicId]);
  const visibleConversations =
    channelFilter === 'all'
      ? conversations
      : conversations.filter((item) => item.channelId === (filteredChannel?.id ?? channelFilter));
  useEffect(() => {
    if (route.tab !== 'dialogs' || !project) return;
    if (selectedId && visibleConversations.some((item) => item.id === selectedId)) return;
    const nextId = visibleConversations[0]?.id;
    selectConversation(nextId);
    const nextConversation = visibleConversations.find((item) => item.id === nextId);
    navigate(
      `${projectPath(project.slug, 'dialogs')}${nextId ? `/${nextConversation?.publicId ?? nextId}` : ''}`,
      true,
    );
  }, [channelFilter, conversations, project?.id]);
  const refresh = async () => {
    if (!project || demoMode) return;
    const [nextOverview, nextConversations] = await Promise.all([
      api<Overview>(`/api/projects/${project.id}/overview`),
      api<{ conversations: Conversation[] }>(`/api/projects/${project.id}/conversations`),
    ]);
    setOverview(nextOverview);
    setConversations(nextConversations.conversations);
    if (!selectedConversationRef.current) {
      const routedConversation = route.conversationId
        ? nextConversations.conversations.find(
            (item) => item.publicId === route.conversationId || item.id === route.conversationId,
          )
        : undefined;
      selectConversation(routedConversation?.id ?? nextConversations.conversations[0]?.id);
    }
  };
  useEffect(() => {
    void refresh();
  }, [project?.id]);
  useEffect(() => {
    if (!project || !selectedId || demoMode) return;
    const conversationId = selectedId;
    const requestVersion = ++messagesRequestVersion.current;
    let cancelled = false;
    setMessages([]);
    setMessagesConversationId(undefined);
    setMessagesError('');
    setHasOlderMessages(false);
    void api<{ messages: ChatMessage[]; hasMore: boolean }>(
      `/api/projects/${project.id}/conversations/${conversationId}/messages?limit=100`,
    )
      .then((result) => {
        if (
          cancelled ||
          messagesRequestVersion.current !== requestVersion ||
          selectedConversationRef.current !== conversationId
        )
          return;
        setMessages(result.messages);
        setMessagesConversationId(conversationId);
        setHasOlderMessages(result.hasMore);
      })
      .catch((error: unknown) => {
        if (
          cancelled ||
          messagesRequestVersion.current !== requestVersion ||
          selectedConversationRef.current !== conversationId
        )
          return;
        setMessages([]);
        setMessagesConversationId(conversationId);
        setMessagesError(error instanceof Error ? error.message : 'Не удалось загрузить переписку');
      });
    return () => {
      cancelled = true;
    };
  }, [project?.id, selectedId, messagesReloadVersion]);
  useEffect(() => {
    if (!project || demoMode) return;
    const stream = new EventSource(`/api/projects/${project.id}/events`);
    stream.addEventListener('assistant.typing', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent<string>).data) as {
          typing?: boolean;
          conversationId?: string;
        };
        const activeConversationId = selectedConversationRef.current;
        if (!payload.conversationId || payload.conversationId === activeConversationId) {
          setTyping(payload.typing === true);
        }
      } catch {
        setTyping(true);
      }
    });
    const syncDialog = () => {
      setTyping(false);
      void refresh();
      const conversationId = selectedConversationRef.current;
      const requestVersion = messagesRequestVersion.current;
      if (conversationId)
        void api<{ messages: ChatMessage[]; hasMore: boolean }>(
          `/api/projects/${project.id}/conversations/${conversationId}/messages?limit=100`,
        )
          .then((result) => {
            if (selectedConversationRef.current !== conversationId || messagesRequestVersion.current !== requestVersion)
              return;
            setMessages((current) => {
              const merged = new Map(current.map((message) => [message.id, message]));
              for (const message of result.messages) merged.set(message.id, message);
              return Array.from(merged.values()).sort(
                (left, right) =>
                  new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime() ||
                  left.id.localeCompare(right.id),
              );
            });
            setMessagesConversationId(conversationId);
            setMessagesError('');
            setHasOlderMessages(result.hasMore);
          })
          .catch(() => undefined);
    };
    stream.addEventListener('message.created', syncDialog);
    stream.addEventListener('message.updated', syncDialog);
    stream.addEventListener('message.read', syncDialog);
    stream.addEventListener('conversation.updated', syncDialog);
    stream.addEventListener('conversation.outcome', syncDialog);
    stream.addEventListener('conversation.mode', syncDialog);
    stream.addEventListener('conversation.ai_session', syncDialog);
    stream.addEventListener('conversation.follow_up', syncDialog);
    stream.addEventListener('manager.notification', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent<string>).data) as {
          kind?: 'success' | 'manager';
          reason?: string;
          occurredAt?: number;
        };
        if (!payload.occurredAt || Date.now() - payload.occurredAt > 120_000) return;
        notify(
          payload.kind === 'success'
            ? `Успех в диалоге: ${payload.reason || 'целевое действие выполнено'}`
            : `Нужен менеджер: ${payload.reason || 'AI передал диалог человеку'}`,
          'info',
        );
        syncDialog();
      } catch {
        syncDialog();
      }
    });
    return () => stream.close();
  }, [project?.id, notify]);
  const loadOlderMessages = async () => {
    if (!project || !selectedId || !messages[0] || !hasOlderMessages || loadingOlderMessages || demoMode) return;
    const conversationId = selectedId;
    const requestVersion = messagesRequestVersion.current;
    const firstMessageId = messages[0].id;
    setLoadingOlderMessages(true);
    try {
      const page = await api<{ messages: ChatMessage[]; hasMore: boolean }>(
        `/api/projects/${project.id}/conversations/${conversationId}/messages?limit=100&before=${encodeURIComponent(firstMessageId)}`,
      );
      if (selectedConversationRef.current !== conversationId || messagesRequestVersion.current !== requestVersion)
        return;
      setMessages((current) => {
        const known = new Set(current.map((message) => message.id));
        return [...page.messages.filter((message) => !known.has(message.id)), ...current];
      });
      setHasOlderMessages(page.hasMore);
    } finally {
      if (selectedConversationRef.current === conversationId && messagesRequestVersion.current === requestVersion)
        setLoadingOlderMessages(false);
    }
  };
  const mutateMode = async (mode: 'bot' | 'manual') => {
    if (!project || !selectedId) return;
    const conversationId = selectedId;
    const previousMode = conversations.find((item) => item.id === conversationId)?.mode;
    setModeMutationId(conversationId);
    setConversations((items) => items.map((item) => (item.id === conversationId ? { ...item, mode } : item)));
    if (demoMode) {
      setModeMutationId(null);
      return;
    }
    try {
      await api(
        `/api/projects/${project.id}/conversations/${conversationId}/mode`,
        { method: 'POST', body: JSON.stringify({ mode }) },
        session.csrfToken,
      );
      await refresh();
      notify(mode === 'manual' ? 'AI остановлен — можно отвечать вручную' : 'AI возобновлён и подхватил переписку');
    } catch {
      if (previousMode) {
        setConversations((items) =>
          items.map((item) => (item.id === conversationId ? { ...item, mode: previousMode } : item)),
        );
      }
      notify('Не удалось изменить режим AI', 'error');
    } finally {
      setModeMutationId((current) => (current === conversationId ? null : current));
    }
  };
  const resetAiSession = async () => {
    if (!project || !selectedId || modeMutationId) return;
    const conversationId = selectedId;
    setModeMutationId(conversationId);
    try {
      if (!demoMode) {
        await api(
          `/api/projects/${project.id}/conversations/${conversationId}/ai-session/reset`,
          { method: 'POST' },
          session.csrfToken,
        );
        await refresh();
        setMessagesReloadVersion((version) => version + 1);
      } else {
        const nextSessionNumber =
          (conversations.find((item) => item.id === conversationId)?.latestAiSession?.number ?? 0) + 1;
        setConversations((items) =>
          items.map((item) =>
            item.id === conversationId
              ? {
                  ...item,
                  aiSessionCount: (item.aiSessionCount ?? 0) + 1,
                  latestAiSession: {
                    id: String(Date.now()),
                    number: nextSessionNumber,
                    status: 'active',
                    openedAt: new Date().toISOString(),
                    estimatedCost: '0',
                  },
                }
              : item,
          ),
        );
        setMessages((items) => [
          ...items,
          {
            id: `technical-${Date.now()}`,
            direction: 'outbound',
            kind: 'technical',
            text: `AI-контекст обновлён. Начата новая AI-сессия №${nextSessionNumber}.`,
            status: 'sent',
            createdAt: new Date().toISOString(),
            attachments: [],
          },
        ]);
      }
      notify('AI-контекст обновлён — следующий ответ начнётся в новой сессии');
    } catch {
      notify('Не удалось обновить AI-контекст', 'error');
    } finally {
      setModeMutationId((current) => (current === conversationId ? null : current));
    }
  };
  const send = (text: string) => {
    if (!project || !selectedId) return;
    if (demoMode) {
      setMessages((items) => [
        ...items,
        {
          id: String(Date.now()),
          direction: 'outbound',
          text,
          status: 'sent',
          createdAt: new Date().toISOString(),
          attachments: [],
        },
      ]);
      return;
    }
    void api(
      `/api/projects/${project.id}/conversations/${selectedId}/messages`,
      { method: 'POST', body: JSON.stringify({ text }) },
      session.csrfToken,
    );
  };
  const routedSelectedConversation = route.conversationId
    ? conversations.find(
        (conversation) => conversation.publicId === route.conversationId || conversation.id === route.conversationId,
      )
    : undefined;
  const selectionMatchesRoute = !route.conversationId || routedSelectedConversation?.id === selectedId;
  return (
    <div className="cabinet">
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
      <Sidebar
        route={route}
        projectSlug={sidebarProject?.slug}
        projects={session.projects}
        projectId={sidebarProject?.id}
        onProjectChange={selectProject}
        dialogCount={sidebarProject?.conversationCount ?? conversations.length}
        navigate={navigate}
        mobileOpen={mobileOpen}
        close={() => setMobileOpen(false)}
      />
      <div className="cabinet__main">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileOpen(true)}>
            <Menu />
          </button>
          <div className="topbar__context">
            <div className="topbar__page">
              <b>{pageTitle(route)}</b>
              <span title={pageDescription(route)} aria-label={pageDescription(route)}>
                <HelpCircle size={15} />
              </span>
            </div>
            {route.tab === 'dialogs' && project && (
              <HeaderSelect
                label="Канал"
                value={channelFilter}
                icon={<Inbox size={17} />}
                options={[
                  { value: 'all', label: 'Все каналы', detail: `${conversations.length} диалогов` },
                  ...overview.channels.map((channel) => ({
                    value: channel.publicId,
                    label: channel.customName || channel.name,
                    detail: channel.provider === 'vk' ? 'ВКонтакте' : 'Telegram',
                    ...(channel.avatarUrl ? { imageUrl: channel.avatarUrl } : {}),
                  })),
                ]}
                onChange={setChannelFilter}
              />
            )}
          </div>
          <div className="topbar__right">
            <ThemeToggle preference={themePreference} onChange={setThemePreference} />
            <AccountMenu
              session={session}
              onLoggedOut={() => setSession({ user: null, projects: [], csrfToken: null })}
            />
          </div>
        </header>
        <main className={route.tab === 'dialogs' ? 'cabinet__content cabinet__content--dialogs' : 'cabinet__content'}>
          {!project ? (
            <ProjectsPage
              projects={session.projects}
              session={session}
              onCreated={() => void loadSession().then(setSession)}
              navigate={navigate}
              notify={notify}
            />
          ) : route.tab === 'dialogs' ? (
            <Dialogs
              project={project}
              overview={overview}
              csrf={session.csrfToken}
              channelId={filteredChannel?.id}
              conversations={visibleConversations}
              messages={selectionMatchesRoute && messagesConversationId === selectedId ? messages : []}
              messagesLoading={Boolean(
                selectedId && (!selectionMatchesRoute || (messagesConversationId !== selectedId && !messagesError)),
              )}
              messagesError={selectionMatchesRoute ? messagesError : ''}
              selectedId={selectedId}
              onSelect={(conversationId) => {
                selectConversation(conversationId);
                const conversation = conversations.find((item) => item.id === conversationId);
                navigate(`${projectPath(project.slug, 'dialogs')}/${conversation?.publicId ?? conversationId}`);
              }}
              onSearchSelect={(result) => {
                setConversations((items) => {
                  if (items.some((item) => item.id === result.conversationId)) return items;
                  return [
                    {
                      id: result.conversationId,
                      publicId: result.publicId,
                      title: result.title,
                      mode: 'manual',
                      botConfigured: false,
                      qualification: 'unknown',
                      automationOutcome: 'active',
                      messageCount: 0,
                      lastMessageAt: result.createdAt,
                      channelId: result.channelId,
                      channelName: result.channelName,
                      provider: result.provider,
                      lastMessage: result.snippet,
                      labelIds: [],
                      ...(result.avatarUrl ? { avatarUrl: result.avatarUrl } : {}),
                    },
                    ...items,
                  ];
                });
                selectConversation(result.conversationId);
                navigate(`${projectPath(project.slug, 'dialogs')}/${result.publicId}`);
              }}
              onRefresh={() => void refresh()}
              onRetryMessages={() => setMessagesReloadVersion((version) => version + 1)}
              onClose={() => {
                selectConversation(undefined);
                navigate(projectPath(project.slug, 'dialogs'));
              }}
              typing={typing}
              onMode={mutateMode}
              modeBusy={modeMutationId === selectedId}
              onResetAiSession={() => void resetAiSession()}
              onSend={send}
              hasOlder={hasOlderMessages}
              loadingOlder={loadingOlderMessages}
              onLoadOlder={loadOlderMessages}
            />
          ) : route.tab === 'analytics' ? (
            <Analytics overview={overview} project={project} projects={session.projects} />
          ) : route.tab === 'projects' ? (
            <ProjectsPage
              projects={session.projects}
              session={session}
              onCreated={() => void loadSession().then(setSession)}
              navigate={navigate}
              notify={notify}
            />
          ) : (
            <SettingsPage
              project={project}
              overview={overview}
              session={session}
              onRefresh={() => void refresh()}
              section={route.settingsSection}
              onSectionChange={(section) => navigate(projectPath(project.slug, 'settings', section))}
              notify={notify}
            />
          )}
        </main>
      </div>
    </div>
  );
}

export function App() {
  const [session, setSession] = useState<Session | null>(demoMode ? demoSession : null);
  const [loading, setLoading] = useState(!demoMode);
  const [error, setError] = useState('');
  useEffect(() => {
    if (demoMode) return;
    loadSession()
      .then(setSession)
      .catch(() => setError('Не удалось связаться с SalesAI'))
      .finally(() => setLoading(false));
  }, []);
  const appRoute = useMemo(() => location.pathname.startsWith('/app'), []);
  if (loading)
    return (
      <div className="boot">
        <Brand />
        <span className="loader" />
      </div>
    );
  if (error || !session)
    return (
      <div className="boot">
        <Brand />
        <p>{error || 'Сервис временно недоступен'}</p>
        <button className="button button--dark" onClick={() => location.reload()}>
          Повторить
        </button>
      </div>
    );
  if (appRoute && session.user) return <Cabinet session={session} setSession={setSession} />;
  if (appRoute && !session.user) {
    location.assign('/api/auth/sso/start?return_path=/app');
    return null;
  }
  return <Landing session={session} onLoggedOut={() => setSession({ user: null, projects: [], csrfToken: null })} />;
}

function relativeTime(value: string) {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60_000));
  return minutes < 1 ? 'сейчас' : minutes < 60 ? `${minutes} мин` : `${Math.floor(minutes / 60)} ч`;
}
function displayConversationTitle(conversation: Pick<Conversation, 'title' | 'provider'>) {
  return conversation.title?.trim() || (conversation.provider === 'vk' ? 'Диалог ВКонтакте' : 'Диалог Telegram');
}
function formatTime(value: string) {
  return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}
